import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Brain, Sparkles, Printer, Trash2, ChevronRight,
  AlertTriangle, Shield, DollarSign, HardDrive,
  Ticket, CheckSquare, RefreshCw, Clock, ArrowLeft,
  TrendingUp, Package, Monitor, BarChart2, Cloud, TrendingDown, FileText, ShieldAlert, LifeBuoy,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// El rol `seguridad` solo puede generar informes de seguridad (espejo del backend).
const SECURITY_REPORT_IDS = ['seguridad', 'seguridad_aws', 'cumplimiento', 'dr_plan'];

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
function tok() { return localStorage.getItem('token'); }
async function apiFetch(path, opts = {}) {
  const res = await fetch(API_URL + path, {
    ...opts,
    headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Error ${res.status}`); }
  return res.json();
}

// ── Configuración de tipos de informe ────────────────────────────────────────

const REPORT_TYPES = [
  { id: 'ejecutivo',    label: 'Informe Ejecutivo',       icon: Brain,       color: 'var(--green)', bg: 'var(--green-dim)', desc: 'Visión general del estado de la infraestructura IT' },
  { id: 'seguridad',    label: 'Informe de Seguridad',    icon: Shield,      color: 'var(--red)', bg: 'var(--red-dim)', desc: 'Vulnerabilidades, CVEs, higiene y score por activo' },
  { id: 'costos',       label: 'Informe de Costos',       icon: DollarSign,  color: 'var(--amber)', bg: 'var(--amber-dim)', desc: 'Cloud, licencias y depreciación de hardware' },
  { id: 'capacity',     label: 'Informe de Capacity',     icon: HardDrive,   color: 'var(--accent)', bg: 'var(--accent-dim)', desc: 'RAM, disco, CPU y tendencias de crecimiento' },
  { id: 'incidentes',   label: 'Informe de Incidentes',   icon: Ticket,      color: 'var(--ink-2)', bg: 'var(--bg-subtle)', desc: 'Tendencias, MTTR y distribución por prioridad' },
  { id: 'cumplimiento', label: 'Informe de Cumplimiento', icon: CheckSquare, color: 'var(--ink-2)', bg: '#ecfeff', desc: 'Baseline, parches, políticas y readiness ISO 27001' },
  { id: 'optimizacion_aws', label: 'Optimización AWS', icon: Cloud, color: 'var(--accent)', bg: 'var(--accent-dim)', desc: 'Optimización de costos AWS por ambiente', needsAccount: true },
  { id: 'seguridad_aws', label: 'Seguridad AWS', icon: ShieldAlert, color: 'var(--red)', bg: 'var(--red-dim)', desc: 'Postura de seguridad AWS: score, IAM, exposición y auditoría', needsAccount: true },
  { id: 'dr_plan', label: 'Plan de Disaster Recovery', icon: LifeBuoy, color: 'var(--ink-2)', bg: '#ecfeff', desc: 'Plan de recuperación por niveles desde la matriz de criticidad', needsLocation: true },
];

const RISK_CFG = {
  CRÍTICO: { color: 'var(--red)', bg: 'var(--red-dim)' },
  ALTO:    { color: 'var(--amber)', bg: 'var(--amber-dim)' },
  MEDIO:   { color: 'var(--accent)', bg: 'var(--accent-dim)' },
  BAJO:    { color: 'var(--green)', bg: 'var(--green-dim)' },
  // Oportunidad de ahorro (informe Optimización AWS)
  ALTA:    { color: 'var(--green)', bg: 'var(--green-dim)' },
  MEDIA:   { color: 'var(--amber)', bg: 'var(--amber-dim)' },
  BAJA:    { color: 'var(--ink-2)', bg: 'var(--border)' },
};

// ── Infografía — estilo RASA + ITIL Suite orange/white ───────────────────────

const BRAND = 'var(--accent)';

const CLR = {
  red:    'var(--red)', redBg:    'var(--red-dim)',
  amber:  'var(--amber)', amberBg:  'var(--amber-dim)',
  green:  'var(--green)', greenBg:  'var(--green-dim)',
  blue:   'var(--accent)', blueBg:   'var(--accent-dim)',
  orange: '#f97316', orangeBg: 'var(--amber-dim)',
  slate:  'var(--ink-2)',
};

function InfWrap({ children }) {
  return (
    <div style={{ background: 'var(--bg-subtle)', borderRadius: 12, padding: '4px 0 8px' }}>
      {children}
    </div>
  );
}

function KpiStrip({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
      {items.map((kpi, i) => (
        <div key={i} style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', borderLeft: `4px solid ${kpi.color || BRAND}`, padding: '16px 18px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{kpi.label}</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: kpi.color || 'var(--ink)', lineHeight: 1 }}>{kpi.value ?? '—'}</div>
          {kpi.sub && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{kpi.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function InfSection({ title, iconColor, children }) {
  const c = iconColor || BRAND;
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', padding: '18px 20px' }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{ width: 22, height: 22, borderRadius: 5, background: c + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: c }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</span>
        </div>
      )}
      {children}
    </div>
  );
}

function MRow({ label, value, color, bg, bar, barMax, last }) {
  const clr = color || 'var(--ink)';
  const pct = (bar != null && barMax) ? Math.min(100, Math.max(0, (bar / barMax) * 100)) : null;
  return (
    <div style={{ borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-2)', maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: clr, flexShrink: 0 }}>{value}</span>
      </div>
      {pct != null && (
        <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: clr, borderRadius: 2 }} />
        </div>
      )}
    </div>
  );
}

function MiniCard({ label, value, color, bg }) {
  return (
    <div style={{ background: bg || 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
      <div style={{ fontSize: 20, fontWeight: 900, color: color || 'var(--ink)', lineHeight: 1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  );
}

function InfBar({ value = 0, max = 100, color = BRAND, height = 8 }) {
  const pct = Math.min(100, Math.max(0, max > 0 ? (value / max) * 100 : 0));
  return (
    <div style={{ height, background: 'var(--border)', borderRadius: height / 2, overflow: 'hidden', marginTop: 6 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: height / 2 }} />
    </div>
  );
}

function ActionPlan({ prioridades }) {
  if (!prioridades?.length) return null;
  const alto  = prioridades.filter(p => p.impacto === 'ALTO');
  const medio = prioridades.filter(p => p.impacto === 'MEDIO');
  const bajo  = prioridades.filter(p => p.impacto === 'BAJO');
  const groups = [
    { items: alto,  label: 'Alta prioridad — esta semana',   bg: CLR.redBg,   border: 'var(--red-dim)', numBg: 'var(--red-dim)', numColor: CLR.red,   textColor: 'var(--red)' },
    { items: medio, label: 'Prioridad media — este mes',     bg: CLR.amberBg, border: 'var(--amber-dim)', numBg: 'var(--amber-dim)', numColor: CLR.amber, textColor: '#78350f' },
    { items: bajo,  label: 'Prioridad baja — próximo ciclo', bg: CLR.blueBg,  border: 'var(--accent-dim)', numBg: 'var(--accent-dim)', numColor: CLR.blue,  textColor: '#1e3a8a' },
  ].filter(g => g.items.length > 0);
  const mid   = Math.ceil(groups.length / 2);
  const left  = groups.slice(0, mid);
  const right = groups.slice(mid);
  const renderGroup = (grp) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{grp.label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {grp.items.map((p, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: grp.bg, border: `1px solid ${grp.border}`, borderRadius: 8, padding: '10px 12px' }}>
            <span style={{ fontSize: 11, fontWeight: 900, color: grp.numColor, flexShrink: 0, marginTop: 1, minWidth: 20, textAlign: 'center' }}>{p.orden}</span>
            <p style={{ fontSize: 12, color: grp.textColor, lineHeight: 1.5, margin: 0 }}>{p.accion}</p>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <InfSection title={`Plan de acción priorizado — ${prioridades.length} acciones`} iconColor={BRAND}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>{left.map((g, i) => <div key={i}>{renderGroup(g)}</div>)}</div>
        <div>{right.length > 0 ? right.map((g, i) => <div key={i}>{renderGroup(g)}</div>) : null}</div>
      </div>
    </InfSection>
  );
}

// ── Infografías por tipo ──────────────────────────────────────────────────────

function InfografiaEjecutivo({ a, raw }) {
  const assets = raw?.assets    || {};
  const inc    = raw?.incidents || {};
  const seg    = raw?.security  || {};
  const pat    = raw?.patches   || {};
  const scoreColor = Number(seg.avg_score||0) >= 75 ? CLR.green : Number(seg.avg_score||0) >= 50 ? CLR.amber : CLR.red;
  return (
    <InfWrap>
      <KpiStrip items={[
        { label: 'Activos totales',     value: assets.total    || 0,                    color: BRAND        },
        { label: 'Security score',      value: `${seg.avg_score||0}/100`,               color: scoreColor   },
        { label: 'Incidentes abiertos', value: inc.open        || 0,                    color: CLR.amber    },
        { label: 'Parches pendientes',  value: pat.pending     || 0,                    color: CLR.red      },
      ]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title="Estado del inventario" iconColor={BRAND}>
          {[
            { label: 'Activos operativos', value: assets.active   || 0, color: CLR.green, bg: CLR.greenBg, bar: Number(assets.active  ||0), barMax: Number(assets.total||1) },
            { label: 'En advertencia',     value: assets.warning  || 0, color: CLR.amber, bg: CLR.amberBg, bar: Number(assets.warning ||0), barMax: Number(assets.total||1) },
            { label: 'Críticos',           value: assets.critical || 0, color: CLR.red,   bg: CLR.redBg,   bar: Number(assets.critical||0), barMax: Number(assets.total||1) },
            { label: 'Sin conexión',       value: assets.offline  || 0, color: CLR.slate, bg: 'var(--border)',   bar: Number(assets.offline ||0), barMax: Number(assets.total||1), last: true },
          ].map((row, i) => <MRow key={i} {...row} />)}
          {a.resumen_ejecutivo && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.6, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              {a.resumen_ejecutivo.slice(0, 220)}{a.resumen_ejecutivo.length > 220 ? '…' : ''}
            </div>
          )}
        </InfSection>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <InfSection title="Postura de seguridad" iconColor={CLR.red}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <MiniCard label="Score prom."   value={`${seg.avg_score||0}`}      color={scoreColor}  bg={scoreColor + '15'} />
              <MiniCard label="Activos crit." value={seg.critical_count || 0}    color={CLR.red}     bg={CLR.redBg}   />
              <MiniCard label="KEV matches"   value={raw?.kev?.kev_matches || 0} color='var(--ink-2)'    bg='var(--bg-subtle)'     />
              <MiniCard label="Score bueno"   value={seg.good_count || 0}        color={CLR.green}   bg={CLR.greenBg} />
            </div>
            <InfBar value={Number(seg.avg_score||0)} color={scoreColor} />
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 4, textAlign: 'right' }}>Score promedio / 100</div>
          </InfSection>
          <InfSection title="Incidentes" iconColor={CLR.amber}>
            {[
              { label: 'Abiertos',     value: inc.open       || 0, color: CLR.amber, bg: CLR.amberBg },
              { label: 'Críticos',     value: inc.critical   || 0, color: CLR.red,   bg: CLR.redBg   },
              { label: 'Cerrados 30d', value: inc.closed_30d || 0, color: CLR.green, bg: CLR.greenBg, last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </InfSection>
          <InfSection title="Estado de parches" iconColor='var(--ink-2)'>
            {[
              { label: 'Al día',      value: pat.up_to_date || 0, color: CLR.green, bg: CLR.greenBg, bar: Number(pat.up_to_date||0), barMax: Number((pat.up_to_date||0)+(pat.pending||0)) || 1 },
              { label: 'Pendientes',  value: pat.pending    || 0, color: CLR.red,   bg: CLR.redBg,   bar: Number(pat.pending||0),    barMax: Number((pat.up_to_date||0)+(pat.pending||0)) || 1, last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </InfSection>
        </div>
      </div>
      <ActionPlan prioridades={a.prioridades} />
    </InfWrap>
  );
}

function InfografiaSeguridad({ a, raw }) {
  const seg         = raw?.security          || {};
  const hyg         = raw?.hygiene           || {};
  const pat         = raw?.patches           || {};
  const kev         = raw?.kev               || {};
  const vulns       = raw?.vulns_by_severity || [];
  const risk_assets = raw?.risk_assets       || [];
  const scoreColor  = Number(seg.avg_score||0) >= 75 ? CLR.green : Number(seg.avg_score||0) >= 50 ? CLR.amber : CLR.red;
  const totalVulnsCH = Number(vulns.find(v=>v.severity==='critical')?.total||0) + Number(vulns.find(v=>v.severity==='high')?.total||0);
  const sevClr = { critical: CLR.red, high: CLR.amber, medium: 'var(--amber)', low: CLR.green, info: 'var(--ink-2)' };
  const maxVuln = Math.max(...vulns.map(v => Number(v.total)), 1);
  return (
    <InfWrap>
      <KpiStrip items={[
        { label: 'Score promedio',     value: `${seg.avg_score||0}/100`, color: scoreColor   },
        { label: 'Activos críticos',   value: seg.critical_count || 0,   color: CLR.red      },
        { label: 'Vulns crít + altas', value: totalVulnsCH,              color: CLR.amber    },
        { label: 'KEV matches',        value: kev.kev_matches    || 0,   color: 'var(--ink-2)'    },
      ]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title="Distribución de security score" iconColor={scoreColor}>
          {[
            { label: 'Score bueno (≥ 75)',    value: seg.good_count     || 0, color: CLR.green, bg: CLR.greenBg, bar: Number(seg.good_count    ||0), barMax: Number(seg.total_assets||1) },
            { label: 'Score medio (50–74)',   value: seg.low_count      || 0, color: CLR.amber, bg: CLR.amberBg, bar: Number(seg.low_count     ||0), barMax: Number(seg.total_assets||1) },
            { label: 'Score crítico (< 50)',  value: seg.critical_count || 0, color: CLR.red,   bg: CLR.redBg,   bar: Number(seg.critical_count||0), barMax: Number(seg.total_assets||1) },
          ].map((row, i, arr) => <MRow key={i} {...row} last={i===arr.length-1} />)}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Estado de parches</div>
            {[
              { label: 'Al día',      value: pat.up_to_date || 0, color: CLR.green, bg: CLR.greenBg },
              { label: 'Pendientes',  value: pat.pending    || 0, color: CLR.red,   bg: CLR.redBg   },
              { label: 'Desconocido', value: pat.unknown    || 0, color: CLR.slate, bg: 'var(--border)', last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </div>
        </InfSection>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <InfSection title="Vulnerabilidades por severidad" iconColor={CLR.red}>
            {vulns.length > 0 ? vulns.map((v, i) => (
              <MRow key={i} label={v.severity.charAt(0).toUpperCase()+v.severity.slice(1)}
                value={v.total} color={sevClr[v.severity]||CLR.slate} bg={(sevClr[v.severity]||CLR.slate)+'18'}
                bar={Number(v.total)} barMax={maxVuln} last={i===vulns.length-1} />
            )) : <div style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center', padding: '12px 0' }}>Sin datos</div>}
          </InfSection>
          <InfSection title="Datos de higiene" iconColor='var(--ink-2)'>
            {[
              { label: 'Activos con vulnerabilidades', value: hyg.assets_with_vulns || 0, color: CLR.red,   bg: CLR.redBg   },
              { label: 'Total vulns críticas + altas',  value: hyg.total_vulns      || 0, color: CLR.amber, bg: CLR.amberBg },
              { label: 'Score mínimo registrado',       value: seg.min_score        || 0, color: CLR.red,   bg: CLR.redBg, last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </InfSection>
        </div>
      </div>
      {risk_assets.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <InfSection title="Activos con menor security score" iconColor={CLR.red}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
              {risk_assets.slice(0, 6).map((asset, i) => {
                const sc = Number(asset.security_score || 0);
                const c  = sc < 50 ? CLR.red : sc < 75 ? CLR.amber : CLR.green;
                return (
                  <div key={i} style={{ background: c + '10', border: `1px solid ${c}30`, borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.hostname}</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: c }}>{sc}</div>
                    <InfBar value={sc} color={c} height={4} />
                  </div>
                );
              })}
            </div>
          </InfSection>
        </div>
      )}
      <ActionPlan prioridades={a.prioridades} />
    </InfWrap>
  );
}

function InfografiaCostos({ a, raw }) {
  const cloud  = raw?.cloud_detail      || [];
  const lic    = raw?.licenses          || {};
  const pur    = raw?.purchases         || {};
  const cats   = raw?.costs_by_category || [];
  const totalCloud = cloud.reduce((s, c) => s + parseFloat(c.monthly_cost||0), 0);
  return (
    <InfWrap>
      <KpiStrip items={[
        { label: 'Cloud total/mes',    value: `$${totalCloud.toFixed(0)}`,                           color: BRAND     },
        { label: 'Licencias anuales',  value: `$${parseFloat(lic.annual_total||0).toFixed(0)}`,      color: CLR.blue  },
        { label: 'Inversión hardware', value: `$${parseFloat(pur.total_invested||0).toFixed(0)}`,    color: 'var(--ink-2)' },
        { label: 'Depreciación/mes',   value: `$${parseFloat(pur.monthly_depreciation||0).toFixed(0)}`, color: CLR.amber },
      ]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title="Costos cloud por proveedor" iconColor={BRAND}>
          {cloud.length > 0 ? cloud.map((c, i) => (
            <MRow key={i} label={`${c.provider} — ${c.type}`}
              value={`$${parseFloat(c.monthly_cost||0).toFixed(0)}/mes`}
              color={BRAND} bg={CLR.orangeBg}
              bar={parseFloat(c.monthly_cost||0)} barMax={totalCloud||1}
              last={i===cloud.length-1} />
          )) : <div style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center', padding: '12px 0' }}>Sin datos de cloud</div>}
          {cats.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Por categoría operativa</div>
              {cats.slice(0, 4).map((c, i) => (
                <MRow key={i} label={c.category}
                  value={`$${parseFloat(c.monthly||0).toFixed(0)}/mes`}
                  color={CLR.slate} bg='var(--border)' last={i===Math.min(cats.length,4)-1} />
              ))}
            </div>
          )}
        </InfSection>
        <InfSection title="Estado de licencias" iconColor={CLR.blue}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            <MiniCard label="Total licencias" value={lic.total         || 0} color={CLR.blue}  bg={CLR.blueBg}  />
            <MiniCard label="Vencidas"        value={lic.expired       || 0} color={CLR.red}   bg={CLR.redBg}   />
            <MiniCard label="Por vencer 30d"  value={lic.expiring_soon || 0} color={CLR.amber} bg={CLR.amberBg} />
            <MiniCard label="Activos con precio" value={pur.total_assets || 0} color='var(--ink-2)' bg='var(--bg-subtle)'    />
          </div>
        </InfSection>
      </div>
      <ActionPlan prioridades={a.prioridades} />
    </InfWrap>
  );
}

function InfografiaCapacity({ a, raw }) {
  const ram    = raw?.ram          || {};
  const disk   = raw?.disk         || {};
  const os     = raw?.os_breakdown || [];
  const grow   = raw?.growth       || [];
  const assets = raw?.assets       || {};
  return (
    <InfWrap>
      <KpiStrip items={[
        { label: 'Total activos',  value: assets.total     || 0,       color: BRAND    },
        { label: 'RAM promedio',   value: `${ram.avg_ram||0} GB`,      color: CLR.blue },
        { label: 'Con < 8 GB RAM', value: ram.low_ram      || 0,       color: CLR.red  },
        { label: 'Con SSD',        value: disk.ssd         || 0,       color: CLR.green },
      ]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title="Distribución de memoria RAM" iconColor={CLR.blue}>
          {[
            { label: 'RAM mínima registrada', value: `${ram.min_ram||0} GB`, color: CLR.slate, bg: 'var(--border)'    },
            { label: 'RAM promedio',          value: `${ram.avg_ram||0} GB`, color: CLR.blue,  bg: CLR.blueBg   },
            { label: 'RAM máxima registrada', value: `${ram.max_ram||0} GB`, color: CLR.green, bg: CLR.greenBg  },
            { label: 'Activos con < 8 GB',   value: ram.low_ram  || 0,      color: CLR.red,   bg: CLR.redBg    },
            { label: 'Activos con ≥ 16 GB',  value: ram.high_ram || 0,      color: CLR.green, bg: CLR.greenBg, last: true },
          ].map((row, i) => <MRow key={i} {...row} />)}
        </InfSection>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <InfSection title="Sistemas operativos" iconColor='var(--ink-2)'>
            {os.length > 0 ? os.slice(0,6).map((o, i) => (
              <MRow key={i} label={o.os_name||'Desconocido'} value={o.total}
                color='var(--ink-2)' bg='#ecfeff'
                bar={Number(o.total)} barMax={Math.max(...os.map(x=>Number(x.total)),1)}
                last={i===Math.min(os.length,6)-1} />
            )) : <div style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center', padding: '12px 0' }}>Sin datos</div>}
          </InfSection>
          <InfSection title="Almacenamiento" iconColor='var(--ink-2)'>
            {[
              { label: 'Con información de disco', value: disk.with_disk_info||0, color: CLR.slate, bg: 'var(--border)', bar: Number(disk.with_disk_info||0), barMax: Number(assets.total||1) },
              { label: 'Con SSD',                  value: disk.ssd            ||0, color: 'var(--ink-2)', bg: 'var(--bg-subtle)', bar: Number(disk.ssd||0),            barMax: Number(disk.with_disk_info||1), last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </InfSection>
          {grow.length > 0 && (
            <InfSection title="Crecimiento mensual" iconColor={CLR.green}>
              {grow.slice(-5).map((g, i, arr) => (
                <MRow key={i}
                  label={new Date(g.month).toLocaleDateString('es-AR',{month:'short',year:'2-digit'})}
                  value={`+${g.new_assets}`} color={CLR.green} bg={CLR.greenBg}
                  bar={Number(g.new_assets)} barMax={Math.max(...grow.map(x=>Number(x.new_assets)),1)}
                  last={i===arr.length-1} />
              ))}
            </InfSection>
          )}
        </div>
      </div>
      <ActionPlan prioridades={a.prioridades} />
    </InfWrap>
  );
}

function InfografiaIncidentes({ a, raw }) {
  const stats      = raw?.stats       || {};
  const byPriority = raw?.by_priority || [];
  const byCat      = raw?.by_category || [];
  const trend      = raw?.trend       || [];
  const topAssets  = raw?.top_assets  || [];
  const priClr = {
    critical: { color: CLR.red,   bg: CLR.redBg   },
    high:     { color: CLR.amber, bg: CLR.amberBg  },
    medium:   { color: 'var(--amber)', bg: 'var(--amber-dim)'    },
    low:      { color: CLR.green, bg: CLR.greenBg  },
  };
  return (
    <InfWrap>
      <KpiStrip items={[
        { label: 'Incidentes totales', value: stats.total       || 0, color: BRAND    },
        { label: 'Abiertos',           value: stats.open        || 0, color: CLR.amber },
        { label: 'Creados 30 días',    value: stats.created_30d || 0, color: CLR.red   },
        { label: 'Cerrados 30 días',   value: stats.closed_30d  || 0, color: CLR.green },
      ]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title="Por prioridad" iconColor='var(--ink-2)'>
          {byPriority.length > 0 ? byPriority.map((p, i) => {
            const cfg = priClr[p.priority] || { color: CLR.slate, bg: 'var(--border)' };
            return (
              <MRow key={i} label={p.priority.charAt(0).toUpperCase()+p.priority.slice(1)}
                value={p.total} color={cfg.color} bg={cfg.bg}
                bar={Number(p.total)} barMax={Math.max(...byPriority.map(x=>Number(x.total)),1)}
                last={i===byPriority.length-1} />
            );
          }) : <div style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center', padding: '12px 0' }}>Sin datos</div>}
          {topAssets.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Top activos con incidentes</div>
              {topAssets.slice(0,4).map((asset, i) => (
                <MRow key={i} label={asset.hostname} value={asset.incident_count}
                  color={CLR.amber} bg={CLR.amberBg}
                  bar={Number(asset.incident_count)} barMax={Math.max(...topAssets.map(x=>Number(x.incident_count)),1)}
                  last={i===Math.min(topAssets.length,4)-1} />
              ))}
            </div>
          )}
        </InfSection>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {byCat.length > 0 && (
            <InfSection title="Por categoría" iconColor='var(--ink-2)'>
              {byCat.slice(0,5).map((c, i) => (
                <MRow key={i} label={c.category} value={c.total}
                  color='var(--ink-2)' bg='#ecfeff'
                  bar={Number(c.total)} barMax={Math.max(...byCat.map(x=>Number(x.total)),1)}
                  last={i===Math.min(byCat.length,5)-1} />
              ))}
            </InfSection>
          )}
          {trend.length > 0 && (
            <InfSection title="Tendencia semanal" iconColor={CLR.blue}>
              {trend.slice(-5).map((t, i, arr) => (
                <MRow key={i}
                  label={new Date(t.week).toLocaleDateString('es-AR',{day:'2-digit',month:'short'})}
                  value={t.total} color={CLR.blue} bg={CLR.blueBg}
                  bar={Number(t.total)} barMax={Math.max(...trend.map(x=>Number(x.total)),1)}
                  last={i===arr.length-1} />
              ))}
            </InfSection>
          )}
        </div>
      </div>
      <ActionPlan prioridades={a.prioridades} />
    </InfWrap>
  );
}

function InfografiaCumplimiento({ a, raw }) {
  const pat  = raw?.patches  || {};
  const base = raw?.baseline || {};
  const lic  = raw?.licenses || {};
  const seg  = raw?.security || {};
  const patchPct  = a.parches?.porcentaje_cumplimiento  ?? (pat.total > 0 ? Math.round(((pat.compliant||pat.up_to_date||0)) / pat.total * 100) : 0);
  const basePct   = a.baseline?.porcentaje_cumplimiento ?? (base.total_checks > 0 ? Math.round((base.passing||0) / base.total_checks * 100) : 0);
  const patchClr  = patchPct >= 80 ? CLR.green : patchPct >= 50 ? CLR.amber : CLR.red;
  const baseClr   = basePct  >= 80 ? CLR.green : basePct  >= 50 ? CLR.amber : CLR.red;
  const scoreColor= Number(seg.avg_score||0) >= 75 ? CLR.green : Number(seg.avg_score||0) >= 50 ? CLR.amber : CLR.red;
  return (
    <InfWrap>
      <KpiStrip items={[
        { label: 'Conformidad parches', value: `${patchPct}%`,           color: patchClr   },
        { label: 'Checks baseline OK',  value: `${basePct}%`,            color: baseClr    },
        { label: 'Licencias vencidas',  value: lic.expired || 0,          color: CLR.red    },
        { label: 'Score promedio',      value: `${seg.avg_score||0}/100`, color: scoreColor },
      ]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title="Cumplimiento de parches" iconColor={patchClr}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Conformidad global</span>
              <span style={{ fontSize: 22, fontWeight: 900, color: patchClr }}>{patchPct}%</span>
            </div>
            <InfBar value={patchPct} color={patchClr} />
          </div>
          {[
            { label: 'Conformes (al día)',   value: pat.compliant||pat.up_to_date||0, color: CLR.green, bg: CLR.greenBg },
            { label: 'No conformes',         value: pat.non_compliant||pat.pending||0, color: CLR.red,  bg: CLR.redBg   },
            { label: 'Sin información',      value: pat.unknown||0,                    color: CLR.slate, bg: 'var(--border)', last: true },
          ].map((row, i) => <MRow key={i} {...row} />)}
        </InfSection>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <InfSection title="Baseline de seguridad" iconColor={baseClr}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Checks pasando</span>
                <span style={{ fontSize: 22, fontWeight: 900, color: baseClr }}>{basePct}%</span>
              </div>
              <InfBar value={basePct} color={baseClr} />
            </div>
            {[
              { label: 'Checks pasando', value: base.passing||0, color: CLR.green, bg: CLR.greenBg },
              { label: 'Checks fallando',value: base.failing||0, color: CLR.red,   bg: CLR.redBg, last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </InfSection>
          <InfSection title="Licencias" iconColor={CLR.amber}>
            {[
              { label: 'Total licencias',    value: lic.total         || 0, color: CLR.slate, bg: 'var(--border)'   },
              { label: 'Vencidas',           value: lic.expired       || 0, color: CLR.red,   bg: CLR.redBg   },
              { label: 'Por vencer (30 d)',   value: lic.expiring_soon || 0, color: CLR.amber, bg: CLR.amberBg, last: true },
            ].map((row, i) => <MRow key={i} {...row} />)}
          </InfSection>
          {a.iso27001?.areas_criticas?.length > 0 && (
            <InfSection title="ISO 27001 — áreas críticas" iconColor='var(--ink-2)'>
              {a.iso27001.areas_criticas.slice(0,3).map((area, i, arr) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: i<arr.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: CLR.red, marginTop: 5, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.4 }}>{area}</span>
                </div>
              ))}
            </InfSection>
          )}
        </div>
      </div>
      <ActionPlan prioridades={a.prioridades} />
    </InfWrap>
  );
}

function InfografiaOptimizacionAws({ a, raw }) {
  const money = (n) => n == null ? '–' : `US$ ${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
  const idle  = raw?.idle  || [];
  const waste = raw?.waste || [];
  const idleSum  = raw?.idleSummary  || {};
  const wasteSum = raw?.wasteSummary || {};
  const spend = raw?.spend || {};
  const oppColor = a?.oportunidad_ahorro === 'ALTA' ? CLR.green : a?.oportunidad_ahorro === 'MEDIA' ? CLR.amber : CLR.slate;
  const topResources = [...idle, ...waste]
    .filter(r => (r.monthlySaving || 0) > 0)
    .sort((x, y) => (y.monthlySaving || 0) - (x.monthlySaving || 0))
    .slice(0, 8);
  const maxSaving = Math.max(...topResources.map(r => r.monthlySaving || 0), 1);

  return (
    <InfWrap>
      <KpiStrip items={[
        { label: `Ambiente ${raw?.account?.environment || ''}`, value: money(spend.monthToDate), color: BRAND, sub: 'Gasto mes en curso' },
        { label: 'Ahorro mensual',  value: money(a?.ahorro_total_mensual_usd), color: CLR.green },
        { label: 'Ahorro anual',    value: money(a?.ahorro_total_anual_usd),   color: CLR.green },
        { label: 'Oportunidad',     value: a?.oportunidad_ahorro || '—',       color: oppColor },
      ]} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <InfSection title={`Recursos idle (${idle.length})`} iconColor={CLR.amber}>
          {Object.keys(idleSum).length > 0
            ? Object.entries(idleSum).map(([svc, v], i, arr) => (
                <MRow key={svc} label={`${svc} — ${v.count} recurso(s)`} value={money(v.saving)}
                  color={CLR.amber} bg={CLR.amberBg} bar={v.saving} barMax={maxSaving} last={i === arr.length - 1} />
              ))
            : <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sin recursos idle</div>}
        </InfSection>
        <InfSection title={`Desperdicio (${waste.length})`} iconColor={CLR.red}>
          {Object.keys(wasteSum).length > 0
            ? Object.entries(wasteSum).map(([svc, v], i, arr) => (
                <MRow key={svc} label={`${svc} — ${v.count} recurso(s)`} value={money(v.saving)}
                  color={CLR.red} bg={CLR.redBg} bar={v.saving} barMax={maxSaving} last={i === arr.length - 1} />
              ))
            : <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sin desperdicio</div>}
        </InfSection>
      </div>

      {topResources.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <InfSection title="Top recursos por ahorro" iconColor={CLR.green}>
            {topResources.map((r, i, arr) => (
              <MRow key={i} label={`${r.service} · ${r.name}`} value={`${money(r.monthlySaving)}/mes`}
                color={CLR.green} bg={CLR.greenBg} bar={r.monthlySaving} barMax={maxSaving} last={i === arr.length - 1} />
            ))}
          </InfSection>
        </div>
      )}

      <ActionPlan prioridades={a?.prioridades} />
    </InfWrap>
  );
}

const INFOGRAFIA = {
  ejecutivo:    InfografiaEjecutivo,
  seguridad:    InfografiaSeguridad,
  costos:       InfografiaCostos,
  capacity:     InfografiaCapacity,
  incidentes:   InfografiaIncidentes,
  cumplimiento: InfografiaCumplimiento,
  optimizacion_aws: InfografiaOptimizacionAws,
};

// ── Vista detallada (recurso por recurso) ─────────────────────────────────────
// ── Dashboard visual de Optimización AWS (HTML autocontenido) ─────────────────
// Función pura: recibe los MISMOS datos que ya produce el generador (analysis + raw_data)
// y devuelve un documento HTML autocontenido (Google Fonts + CSS/JS inline). Se usa tanto
// para el render on-screen (iframe) como para Imprimir/PDF (contentWindow.print()).
const DASH_PAL = ['#0D9488', '#0284C7', '#7C3AED', '#D97706', '#DC2626', '#16A34A', '#0891B2', '#DB2777', '#65A30D', '#9333EA'];

function dEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
function dUsd(n) {
  if (n == null || isNaN(n)) return '—';
  return 'US$ ' + Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function dUsd0(n) {
  if (n == null || isNaN(n)) return '—';
  return 'US$ ' + Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 });
}
function dInt(n) { return Number(n || 0).toLocaleString('es-AR'); }

const DASH_CHEV = '<svg class="chev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>';

function dashMore(obj) {
  if (!obj) return '';
  const recs = (obj.recomendaciones || []).map(r => `<li>${dEsc(r)}</li>`).join('');
  const body = [
    obj.analisis ? `<p>${dEsc(obj.analisis)}</p>` : '',
    obj.justificacion ? `<p class="just"><b>Justificación:</b> ${dEsc(obj.justificacion)}</p>` : '',
    recs ? `<ul>${recs}</ul>` : '',
  ].join('');
  if (!body) return '';
  return `<details class="more"><summary><span class="o">Leer más</span><span class="c">Leer menos</span>${DASH_CHEV}</summary><div class="more-body">${body}</div></details>`;
}

// ¿La app está en tema oscuro/starlink? Para adaptar el HTML autocontenido del iframe.
const appDark = () => { try { return ['dark', 'starlink'].includes(document.documentElement.getAttribute('data-theme')); } catch { return false; } };
// Override oscuro para los dashboards autocontenidos (redefine paleta + literales claros).
const DASH_DARK = `
  :root{--bg:#0a0a0a;--panel:#141416;--border:#2a2a30;--ink:#f0f0f0;--muted:#a0a0a6;--faint:#6b6b72;--amber:#e0a93b;--coral:#e06666;--green:#3ddc84;}
  body{background:#0a0a0a;color:#f0f0f0}
  .card{background:#141416 !important;background-image:none !important}
  .card::before{background:rgba(20,20,22,.86) !important}
  .head,.kpi,.sev,.realcost>div{background:#141416 !important;border-color:#2a2a30 !important}
  .realcost>div:first-child{background:rgba(13,148,136,.14) !important;border-color:#0d9488 !important}
  .count{background:#2a2a30 !important}`;
const darkInject = () => appDark() ? `<style>${DASH_DARK}</style>` : '';

// CSS compartido por los dashboards autocontenidos (Optimización y Seguridad AWS)
const DASH_STYLE = `
  :root{--teal:#0D9488;--blue:#0284C7;--amber:var(--amber);--coral:var(--red);--violet:#7C3AED;--green:var(--green);
    --bg:#FFFFFF;--panel:#FFFFFF;--border:#E4E9F1;--ink:var(--ink);--muted:var(--ink-2);--faint:var(--ink-3);}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'IBM Plex Sans',system-ui,sans-serif;background:var(--bg);color:var(--ink);font-size:14px;line-height:1.55;padding:4px}
  .mono{font-family:'IBM Plex Mono',ui-monospace,monospace;font-variant-numeric:tabular-nums}
  h1,h2,h3,.gauge-num,.donut-num,.kpi-v{font-family:'Space Grotesk',sans-serif}
  .muted{color:var(--muted)} .faint{color:var(--faint)} .right{text-align:right}
  .empty{color:var(--faint);font-size:13px;padding:10px 2px}
  .wrap{max-width:1000px;margin:0 auto;display:flex;flex-direction:column;gap:14px}
  .card{background:var(--panel);border:1px solid var(--border);border-radius:12px;padding:18px 20px;
    box-shadow:0 1px 2px rgba(15,23,42,.04);
    background-image:linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px);
    background-size:28px 28px;background-position:-1px -1px;background-blend-mode:normal;position:relative}
  .card::before{content:"";position:absolute;inset:0;border-radius:12px;background:rgba(255,255,255,.86);z-index:0}
  .card>*{position:relative;z-index:1}
  .card-h{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);margin-bottom:14px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .count{font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--muted);background:var(--border);border-radius:20px;padding:1px 8px}
  .card-sub{font-size:11.5px;color:var(--muted);margin:-4px 0 12px;line-height:1.5}
  .realcost{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px}
  .realcost>div{border:1px solid var(--border);border-radius:8px;padding:10px 12px;display:flex;flex-direction:column;gap:3px}
  .realcost>div:first-child{border-color:#99F6E4;background:#F0FDFA}
  .rc-l{font-size:10px;color:var(--faint);text-transform:uppercase;letter-spacing:.03em}
  .rc-v{font-size:19px;font-weight:600;color:var(--teal)}
  .head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;
    border:1px solid var(--border);border-radius:12px;padding:18px 22px;border-left:4px solid var(--teal);background:#fff}
  .brand{font-family:'Space Grotesk';font-size:11px;font-weight:700;letter-spacing:.14em;color:var(--teal)}
  .head h1{font-size:22px;font-weight:700;margin:4px 0 2px}
  .head .meta{font-size:12px;color:var(--faint)}
  .opp{text-align:right}.opp .l{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--faint);margin-bottom:5px}
  .opp .v{font-family:'Space Grotesk';font-size:15px;font-weight:700;padding:5px 16px;border-radius:8px}
  .hero{display:grid;grid-template-columns:180px 1fr;gap:20px;align-items:center}
  .gauge{width:160px;height:160px;display:block;margin:0 auto}
  .gauge-num{font-size:38px;font-weight:700;fill:var(--ink)}.gauge-pct{font-size:18px;fill:var(--muted)}
  .gauge-cap{font-size:11px;fill:var(--faint);font-family:'IBM Plex Sans'}
  .gauge-grade{font-size:16px;font-weight:700;font-family:'Space Grotesk'}
  .kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
  .kpi{border:1px solid var(--border);border-radius:10px;padding:12px 14px;background:#fff}
  .kpi-v{font-size:22px;font-weight:700;line-height:1.1}
  .kpi-l{font-size:11px;color:var(--muted);margin-top:3px}
  .two{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .donut-wrap{display:flex;gap:16px;align-items:center;flex-wrap:wrap}
  .donut{width:140px;height:140px;flex-shrink:0}
  .donut-num{font-family:'Space Grotesk';font-size:16px;font-weight:700;fill:var(--ink)}
  .donut-cap{font-size:9px;fill:var(--faint);font-family:'IBM Plex Sans'}
  .legend{flex:1;min-width:170px;display:flex;flex-direction:column;gap:5px}
  .leg-row{display:flex;align-items:center;gap:8px;font-size:12px}
  .leg-dot{width:9px;height:9px;border-radius:3px;flex-shrink:0}
  .leg-name{flex:1;color:var(--ink)}.leg-pct{font-family:'IBM Plex Mono';color:var(--muted);width:48px;text-align:right}
  .leg-amt{font-family:'IBM Plex Mono';color:var(--ink);width:80px;text-align:right;font-weight:500}
  .hbar{margin-bottom:11px}.hbar:last-child{margin-bottom:0}
  .hbar-top{display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px;gap:10px}
  .hbar-top .mono{font-weight:600}
  .hbar-track{height:9px;background:var(--border);border-radius:6px;overflow:hidden}
  .hbar-fill{height:100%;border-radius:6px}
  .badge-n{font-family:'IBM Plex Mono';font-size:10px;color:var(--muted);margin-right:4px}
  .err-p{font-size:11px;max-width:76%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block}
  .tbl-wrap{overflow-x:auto}
  .tbl{width:100%;border-collapse:collapse;font-size:12.5px}
  .tbl th{text-align:left;font-weight:600;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.03em;padding:8px 10px;border-bottom:1px solid var(--border);background:var(--bg-subtle);white-space:nowrap}
  .tbl td{padding:9px 10px;border-bottom:1px solid var(--border);vertical-align:middle}
  .tbl tr:last-child td{border-bottom:none}
  .svc{font-weight:600;font-size:11px;color:var(--ink-2);background:var(--border);border-radius:5px;padding:2px 7px}
  .save{color:var(--green);font-weight:600;white-space:nowrap}
  .tag{font-size:10.5px;font-weight:600;white-space:nowrap;background:none !important;border:none !important;padding:0}
  .cpu-cell{min-width:120px}
  .pbar{display:inline-block;width:70px;height:8px;background:var(--border);border-radius:5px;overflow:hidden;vertical-align:middle;margin-right:7px}
  .pbar-fill{height:100%;border-radius:5px}.pbar-v{font-size:11px;color:var(--muted)}
  .log-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px}
  .log-stats>div{border:1px solid var(--border);border-radius:8px;padding:8px 10px;display:flex;flex-direction:column;gap:2px}
  .ls-l{font-size:10px;color:var(--faint);text-transform:uppercase;letter-spacing:.04em}.ls-v{font-size:17px;font-weight:600}
  .err-bars{margin-bottom:14px}
  .term{background:#0B1220;border-radius:10px;overflow:hidden;border:1px solid var(--ink)}
  .term-bar{display:flex;align-items:center;gap:6px;padding:8px 12px;background:#111C2E;border-bottom:1px solid var(--ink)}
  .term-bar .dot{width:10px;height:10px;border-radius:50%}.dot.r{background:#FF5F56}.dot.y{background:#FFBD2E}.dot.g{background:#27C93F}
  .term-title{font-family:'IBM Plex Mono';font-size:11px;color:var(--ink-2);margin-left:8px}
  .term-body{padding:12px 14px;font-family:'IBM Plex Mono';font-size:12px;color:var(--ink-3);line-height:1.7;max-height:none}
  .term-body .ln{white-space:pre-wrap;word-break:break-word}.term-body .pr{color:var(--teal)}
  .find-card{border-left:4px solid var(--coral)}
  .find-list{list-style:none;display:flex;flex-direction:column;gap:8px}
  .find-list li{display:flex;gap:9px;font-size:13px;color:var(--ink-2);align-items:flex-start}
  .find-mark{width:7px;height:7px;border-radius:50%;background:var(--coral);margin-top:6px;flex-shrink:0}
  .prio-list{display:flex;flex-direction:column;gap:8px}
  .prio{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border);font-size:13px}
  .prio:last-child{border-bottom:none}
  .prio-n{width:22px;height:22px;flex-shrink:0;border-radius:6px;background:var(--border);font-family:'IBM Plex Mono';font-weight:600;font-size:12px;display:flex;align-items:center;justify-content:center;color:var(--muted)}
  .prio-a{flex:1;color:var(--ink-2)}.prio-s{color:var(--green);font-weight:600;white-space:nowrap}
  .intro .lede{font-size:14.5px;line-height:1.7;color:var(--ink-2)}
  .more{margin-top:14px}
  .more summary{list-style:none;display:inline-flex;align-items:center;gap:7px;cursor:pointer;
    font-size:12px;font-weight:600;color:var(--teal);background:#F0FDFA;border:1px solid #99F6E4;
    border-radius:999px;padding:5px 14px;user-select:none;transition:background .15s}
  .more summary:hover{background:#CCFBF1}
  .more summary::-webkit-details-marker{display:none}
  .more .chev{transition:transform .2s}
  .more[open] .chev{transform:rotate(180deg)}
  .more .c{display:none}.more[open] .o{display:none}.more[open] .c{display:inline}
  .more-body{margin-top:12px;font-size:13px;line-height:1.7;color:var(--ink-2)}
  .more-body p{margin-bottom:9px}.more-body .just{color:var(--muted)}
  .more-body ul{margin:4px 0 0 18px}.more-body li{margin-bottom:5px}
  .foot{background:linear-gradient(90deg,#F0FDFA,#ECFDF5);border:1px solid #99F6E4;border-radius:12px;padding:18px 22px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px}
  .foot .fl{font-size:12px;color:var(--muted)}
  .foot .fv{font-family:'Space Grotesk';font-size:26px;font-weight:700;color:var(--teal)}
  .foot .fa{font-family:'IBM Plex Mono';font-size:13px;color:var(--muted)}
  .credit{text-align:center;font-size:10px;color:var(--faint);padding:4px 0 8px}
  /* seguridad */
  .chips{display:flex;gap:8px;flex-wrap:wrap}
  .chip{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;padding:5px 12px;border-radius:20px}
  .chip.ok{background:var(--green-dim);color:var(--green);border:1px solid var(--green-dim)}
  .chip.bad{background:var(--red-dim);color:#991B1B;border:1px solid var(--red-dim)}
  .sev{border-left:4px solid var(--muted);border-radius:8px;padding:10px 14px;margin-bottom:8px;background:#fff;border:1px solid var(--border)}
  .sev .sev-h{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:var(--ink);flex-wrap:wrap}
  .sev .sev-svc{font-size:10px;font-weight:700;text-transform:uppercase}
  .sev .sev-d{font-size:12px;color:var(--muted);margin-top:2px}
  @media (max-width:640px){
    .hero{grid-template-columns:1fr}.two{grid-template-columns:1fr}
    .kpis{grid-template-columns:repeat(2,1fr)}.log-stats{grid-template-columns:repeat(2,1fr)}
    .head h1{font-size:19px}.card{background-size:22px 22px}
  }
  @media print{body{padding:0}.card{box-shadow:none}.more>summary{display:none}.more-body{margin-top:0}.more:not([open]) .more-body{display:block}}
`;

function buildAwsOptimizationDashboard(a, raw, report) {
  a = a || {}; raw = raw || {};
  const account = raw.account || {};
  const spend = raw.spend || {};
  const idle = raw.idle || [];
  const waste = raw.waste || [];
  const scheduling = raw.scheduling || [];
  const logs = (raw.logs && !raw.logs.error) ? raw.logs : null;

  const gastoTotal = (spend.monthToDate != null ? spend.monthToDate : a.gasto_mensual_usd) || 0;
  const ahorroMes  = a.ahorro_total_mensual_usd != null ? a.ahorro_total_mensual_usd : (raw.totalMonthlySaving || 0);
  const ahorroAnual = a.ahorro_total_anual_usd != null ? a.ahorro_total_anual_usd : ahorroMes * 12;
  const gaugePct = gastoTotal > 0 ? Math.max(0, Math.min(100, ahorroMes / gastoTotal * 100)) : 0;

  const snaps = waste.filter(w => w.service === 'Snapshot');
  const ebsEip = waste.filter(w => w.service === 'EBS' || w.service === 'EIP');
  const sumSave = arr => arr.reduce((s, r) => s + (r.monthlySaving || 0), 0);

  const OPP = { ALTA: { c:'var(--red)', bg:'var(--red-dim)' }, MEDIA: { c:'var(--amber)', bg:'var(--amber-dim)' }, BAJA: { c:'var(--green)', bg:'var(--green-dim)' } };
  const opp = OPP[String(a.oportunidad_ahorro || '').toUpperCase()] || OPP.MEDIA;

  // ── Hero: gauge SVG ──
  const R = 64, C = 2 * Math.PI * R, dash = C * gaugePct / 100;
  const gauge = `
    <svg viewBox="0 0 160 160" class="gauge">
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="#EEF2F7" stroke-width="14"/>
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="var(--teal)" stroke-width="14" stroke-linecap="round"
        stroke-dasharray="${dash.toFixed(1)} ${(C - dash).toFixed(1)}" transform="rotate(-90 80 80)"/>
      <text x="80" y="74" text-anchor="middle" class="gauge-num">${gaugePct.toFixed(0)}<tspan class="gauge-pct">%</tspan></text>
      <text x="80" y="98" text-anchor="middle" class="gauge-cap">del gasto</text>
    </svg>`;

  // ── KPIs ──
  const kpis = [
    { v: dUsd0(ahorroMes), l: 'Ahorro potencial/mes', c: 'var(--teal)' },
    { v: dInt(gastoTotal ? Math.round(gastoTotal) : 0), l: 'Gasto total (US$)', c: 'var(--ink)' },
    { v: dInt(idle.length), l: 'Recursos idle', c: 'var(--amber)' },
    { v: dInt(snaps.length), l: 'Snapshots huérfanos', c: 'var(--violet)' },
    { v: dInt(scheduling.length), l: 'Candidatos a apagado', c: 'var(--blue)' },
    { v: logs ? dInt(logs.totalErrors) : '—', l: 'Errores (24h)', c: logs && logs.totalErrors > 0 ? 'var(--coral)' : 'var(--faint)' },
  ].map(k => `<div class="kpi"><div class="kpi-v" style="color:${k.c}">${k.v}</div><div class="kpi-l">${k.l}</div></div>`).join('');

  // ── Dona de gasto por servicio ──
  const svcs = (spend.byService || []).filter(s => s.amount > 0);
  const svcTotal = svcs.reduce((s, x) => s + x.amount, 0) || 1;
  const DR = 55, DC = 2 * Math.PI * DR;
  let acc = 0;
  const donutSegs = svcs.map((s, i) => {
    const frac = s.amount / svcTotal, seg = DC * frac;
    const el = `<circle cx="70" cy="70" r="${DR}" fill="none" stroke="${DASH_PAL[i % DASH_PAL.length]}" stroke-width="20"
      stroke-dasharray="${seg.toFixed(1)} ${(DC - seg).toFixed(1)}" stroke-dashoffset="${(-acc).toFixed(1)}" transform="rotate(-90 70 70)"/>`;
    acc += seg; return el;
  }).join('');
  const donutLegend = svcs.map((s, i) => `
    <div class="leg-row"><span class="leg-dot" style="background:${DASH_PAL[i % DASH_PAL.length]}"></span>
      <span class="leg-name">${dEsc(s.service)}</span>
      <span class="leg-pct">${(s.amount / svcTotal * 100).toFixed(1)}%</span>
      <span class="leg-amt">${dUsd0(s.amount)}</span></div>`).join('');
  const donutBlock = svcs.length === 0
    ? `<div class="empty">Sin datos de facturación (Cost Explorer no disponible).</div>`
    : `<div class="donut-wrap"><svg viewBox="0 0 140 140" class="donut">${donutSegs}
        <text x="70" y="66" text-anchor="middle" class="donut-num">${dUsd0(svcTotal)}</text>
        <text x="70" y="84" text-anchor="middle" class="donut-cap">mes en curso</text></svg>
       <div class="legend">${donutLegend}</div></div>`;

  // ── Barras: ahorro por categoría ──
  const cats = [
    { l: 'Recursos idle', v: sumSave(idle), c: 'var(--amber)' },
    { l: 'Snapshots huérfanos', v: sumSave(snaps), c: 'var(--violet)' },
    { l: 'Apagado programado', v: raw.schedulingSaving != null ? raw.schedulingSaving : sumSave(scheduling), c: 'var(--blue)' },
    { l: 'EBS / IPs sin usar', v: sumSave(ebsEip), c: 'var(--coral)' },
  ].filter(c => c.v > 0);
  const catMax = Math.max(...cats.map(c => c.v), 1);
  const catBars = cats.length === 0 ? `<div class="empty">Sin ahorro estimable.</div>` : cats.map(c => `
    <div class="hbar"><div class="hbar-top"><span>${c.l}</span><span class="mono">${dUsd0(c.v)}</span></div>
      <div class="hbar-track"><div class="hbar-fill" style="width:${(c.v / catMax * 100).toFixed(1)}%;background:${c.c}"></div></div></div>`).join('');

  // ── Top por costo de snapshots (agrupado por volumen de origen) ──
  const byOrigin = {};
  snaps.forEach(s => { const k = s.origin || 'vol-desconocido'; (byOrigin[k] = byOrigin[k] || { save: 0, n: 0 }); byOrigin[k].save += s.monthlySaving || 0; byOrigin[k].n++; });
  const originRows = Object.entries(byOrigin).map(([k, v]) => ({ k, ...v })).sort((x, y) => y.save - x.save).slice(0, 10);
  const originMax = Math.max(...originRows.map(r => r.save), 1);
  const realCost = raw.snapshotRealCost;
  const estTotal = sumSave(snaps);
  const snapFactor = (realCost != null && estTotal > 0) ? realCost / estTotal : null;
  const realBanner = realCost != null ? `
      <div class="realcost">
        <div><span class="rc-l">Costo REAL (Cost Explorer${raw.snapshotRealPeriod ? ', mes cerrado' : ''})</span><span class="rc-v mono">${dUsd0(realCost)}/mes</span></div>
        <div><span class="rc-l">Estimado por volumen (bruto)</span><span class="rc-v mono muted">${dUsd0(estTotal)}/mes</span></div>
      </div>` : '';
  const subNote = snapFactor != null
    ? `Ranking por volumen de origen. El costo por volumen es <b>estimado</b> (tamaño × US$0,05/GB-mes) reescalado por el factor real/estimado (×${snapFactor.toFixed(3)}) para que sume el total facturado. Es aproximado — Cost Explorer no desglosa por volumen; para exacto se necesita CUR con resource IDs.`
    : `Ranking por volumen de origen — <b>costo estimado</b> (tamaño del volumen × US$0,05/GB-mes) para priorizar la limpieza.`;
  const snapBlock = originRows.length === 0 ? '' : `
    <div class="card">
      <div class="card-h">Snapshots huérfanos <span class="count">${snaps.length}</span></div>
      ${realBanner}
      <div class="card-sub">${subNote}</div>
      ${originRows.map(r => {
        const corr = snapFactor != null ? r.save * snapFactor : r.save;
        return `
        <div class="hbar"><div class="hbar-top"><span class="mono">${dEsc(r.k)}</span><span><span class="badge-n">${r.n} snap</span> <span class="mono">${dUsd0(corr)}/mes</span>${snapFactor != null ? ` <span class="mono muted" style="font-size:10px">(bruto ${dUsd0(r.save)})</span>` : ''}</span></div>
          <div class="hbar-track"><div class="hbar-fill" style="width:${(r.save / originMax * 100).toFixed(1)}%;background:var(--violet)"></div></div></div>`;
      }).join('')}
      ${dashMore(a.desperdicio)}
    </div>`;

  // ── Tabla recursos idle ──
  const idleRow = (r) => {
    const isEc2 = r.service === 'EC2';
    const m = /([\d.]+)\s*%/.exec(r.metric || '');
    const cpu = isEc2 && m ? parseFloat(m[1]) : null;
    let tag = { l: 'Sin actividad', c: 'var(--amber)' };
    if (cpu != null) {
      if (cpu < 1) tag = { l: 'Prácticamente apagado', c: 'var(--coral)' };
      else if (cpu < 3) tag = { l: 'Uso muy bajo', c: 'var(--amber)' };
      else tag = { l: 'Subutilizado', c: 'var(--blue)' };
    }
    const bar = cpu != null
      ? `<div class="pbar"><div class="pbar-fill" style="width:${Math.max(2, Math.min(100, cpu)).toFixed(0)}%;background:${tag.c}"></div></div><span class="mono pbar-v">${cpu.toFixed(1)}%</span>`
      : `<span class="mono muted">${dEsc(r.metric || '—')}</span>`;
    return `<tr>
      <td><span class="svc">${dEsc(r.service)}</span></td>
      <td class="mono">${dEsc(r.name)}</td>
      <td class="cpu-cell">${bar}</td>
      <td><span class="tag" style="color:${tag.c};border-color:${tag.c}40;background:${tag.c}0f">${tag.l}</span></td>
      <td class="mono right save">${(r.monthlySaving || 0) > 0 ? dUsd0(r.monthlySaving) : '—'}</td>
    </tr>`;
  };
  const idleBlock = `
    <div class="card">
      <div class="card-h">Recursos idle (subutilizados) <span class="count">${idle.length}</span></div>
      ${idle.length === 0 ? `<div class="empty">No se detectaron recursos idle.</div>` : `
      <div class="tbl-wrap"><table class="tbl">
        <thead><tr><th>Servicio</th><th>Recurso</th><th>Uso (CPU / métrica)</th><th>Interpretación</th><th class="right">Ahorro/mes</th></tr></thead>
        <tbody>${idle.map(idleRow).join('')}</tbody>
      </table></div>`}
      ${dashMore(a.idle)}
    </div>`;

  // ── Tabla apagado programado ──
  const schedBlock = scheduling.length === 0 ? '' : `
    <div class="card">
      <div class="card-h">Candidatos a apagado programado <span class="count">${scheduling.length}</span></div>
      <div class="tbl-wrap"><table class="tbl">
        <thead><tr><th>Instancia</th><th>Tipo</th><th>Ventana sugerida</th><th>Ocioso</th><th class="right">Ahorro/mes</th></tr></thead>
        <tbody>${scheduling.map(r => `<tr>
          <td class="mono">${dEsc(r.name)}</td>
          <td class="muted">${dEsc(r.type)}</td>
          <td class="mono" style="color:var(--blue)">${dEsc(r.recommendedWindow)}</td>
          <td class="cpu-cell"><div class="pbar"><div class="pbar-fill" style="width:${Math.max(2, Math.min(100, r.idlePct || 0)).toFixed(0)}%;background:var(--blue)"></div></div><span class="mono pbar-v">${r.idlePct}%</span></td>
          <td class="mono right save">${dUsd0(r.monthlySaving)}</td>
        </tr>`).join('')}</tbody>
      </table></div>
      ${dashMore(a.apagado_programado)}
    </div>`;

  // ── EBS/EIP huérfanos (no perder estos datos) ──
  const ebsBlock = ebsEip.length === 0 ? '' : `
    <div class="card">
      <div class="card-h">Otros recursos huérfanos (EBS / Elastic IP) <span class="count">${ebsEip.length}</span></div>
      <div class="tbl-wrap"><table class="tbl">
        <thead><tr><th>Servicio</th><th>Recurso</th><th>Detalle</th><th class="right">Ahorro/mes</th></tr></thead>
        <tbody>${ebsEip.map(r => `<tr>
          <td><span class="svc">${dEsc(r.service)}</span></td>
          <td class="mono">${dEsc(r.name)}</td>
          <td class="muted">${dEsc(r.detail || r.metric || '')}</td>
          <td class="mono right save">${(r.monthlySaving || 0) > 0 ? dUsd0(r.monthlySaving) : '—'}</td>
        </tr>`).join('')}</tbody>
      </table></div>
    </div>`;

  // ── Logs ──
  const topErr = (logs?.topErrors || []).slice(0, 10);
  const errMax = Math.max(...topErr.map(e => e.count), 1);
  const logsTerminal = a.logs?.analisis
    ? `<div class="term"><div class="term-bar"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="term-title">analysis --logs ${dEsc(logs?.logGroup || '')}</span></div>
        <div class="term-body">${String(a.logs.analisis).split('\n').filter(Boolean).map(l => `<div class="ln"><span class="pr">$</span> ${dEsc(l)}</div>`).join('')}</div></div>`
    : '';
  const logsBlock = !logs ? '' : `
    <div class="card">
      <div class="card-h">Análisis de logs <span class="mono muted" style="font-weight:400">${dEsc(logs.logGroup)}</span></div>
      <div class="log-stats">
        <div><span class="ls-l">Eventos</span><span class="ls-v mono">${dInt(logs.total)}</span></div>
        <div><span class="ls-l">Errores</span><span class="ls-v mono" style="color:var(--coral)">${dInt(logs.totalErrors)}</span></div>
        <div><span class="ls-l">Tasa</span><span class="ls-v mono" style="color:${logs.errorRate > 5 ? 'var(--coral)' : 'var(--ink)'}">${logs.errorRate}%</span></div>
        <div><span class="ls-l">Picos</span><span class="ls-v mono">${(logs.spikes || []).length}</span></div>
      </div>
      ${topErr.length ? `<div class="err-bars">${topErr.map(e => `
        <div class="hbar"><div class="hbar-top"><span class="mono err-p">${dEsc(e.pattern)}</span><span class="mono">${e.count}×</span></div>
          <div class="hbar-track"><div class="hbar-fill" style="width:${(e.count / errMax * 100).toFixed(1)}%;background:var(--coral)"></div></div></div>`).join('')}</div>` : ''}
      ${logsTerminal}
      ${dashMore(a.logs)}
    </div>`;

  // ── Hallazgos críticos (no perder) ──
  const findings = (a.hallazgos_criticos || []).filter(Boolean);
  const findBlock = findings.length === 0 ? '' : `
    <div class="card find-card">
      <div class="card-h">Hallazgos críticos</div>
      <ul class="find-list">${findings.map(f => `<li><span class="find-mark"></span>${dEsc(f)}</li>`).join('')}</ul>
    </div>`;

  // ── Prioridades (no perder) ──
  const IMP = { ALTO:'var(--coral)', MEDIO:'var(--amber)', BAJO:'var(--green)' };
  const prios = (a.prioridades || []);
  const prioBlock = prios.length === 0 ? '' : `
    <div class="card">
      <div class="card-h">Plan de acción priorizado</div>
      <div class="prio-list">${prios.map(p => `
        <div class="prio"><span class="prio-n">${dEsc(p.orden)}</span>
          <span class="prio-a">${dEsc(p.accion)}</span>
          <span class="tag" style="color:${IMP[String(p.impacto || '').toUpperCase()] || 'var(--muted)'};border-color:${IMP[String(p.impacto || '').toUpperCase()] || 'var(--muted)'}40">${dEsc(p.impacto || '')}</span>
          <span class="mono prio-s">${(p.ahorro_usd || 0) > 0 ? dUsd0(p.ahorro_usd) : ''}</span></div>`).join('')}</div>
    </div>`;

  const genDate = report?.created_at ? new Date(report.created_at).toLocaleString('es-AR') : '';
  const resumen = a.resumen_ejecutivo ? `<div class="card intro"><div class="card-h">Resumen ejecutivo</div><p class="lede">${dEsc(a.resumen_ejecutivo)}</p></div>` : '';
  const detMore = a.analisis_detallado
    ? `<div class="card"><div class="card-h">Análisis técnico detallado</div>${dashMore({ analisis: a.analisis_detallado })}</div>` : '';

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  :root{--teal:#0D9488;--blue:#0284C7;--amber:var(--amber);--coral:var(--red);--violet:#7C3AED;--green:var(--green);
    --bg:#FFFFFF;--panel:#FFFFFF;--border:#E4E9F1;--ink:var(--ink);--muted:var(--ink-2);--faint:var(--ink-3);}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'IBM Plex Sans',system-ui,sans-serif;background:var(--bg);color:var(--ink);font-size:14px;line-height:1.55;padding:4px}
  .mono{font-family:'IBM Plex Mono',ui-monospace,monospace;font-variant-numeric:tabular-nums}
  h1,h2,h3,.gauge-num,.donut-num,.kpi-v{font-family:'Space Grotesk',sans-serif}
  .muted{color:var(--muted)} .faint{color:var(--faint)} .right{text-align:right}
  .empty{color:var(--faint);font-size:13px;padding:10px 2px}
  .wrap{max-width:1000px;margin:0 auto;display:flex;flex-direction:column;gap:14px}
  .card{background:var(--panel);border:1px solid var(--border);border-radius:12px;padding:18px 20px;
    box-shadow:0 1px 2px rgba(15,23,42,.04);
    background-image:linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px);
    background-size:28px 28px;background-position:-1px -1px;background-blend-mode:normal;position:relative}
  .card::before{content:"";position:absolute;inset:0;border-radius:12px;background:rgba(255,255,255,.86);z-index:0}
  .card>*{position:relative;z-index:1}
  .card-h{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);margin-bottom:14px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .count{font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--muted);background:var(--border);border-radius:20px;padding:1px 8px}
  .card-sub{font-size:11.5px;color:var(--muted);margin:-4px 0 12px;line-height:1.5}
  .realcost{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px}
  .realcost>div{border:1px solid var(--border);border-radius:8px;padding:10px 12px;display:flex;flex-direction:column;gap:3px}
  .realcost>div:first-child{border-color:#99F6E4;background:#F0FDFA}
  .rc-l{font-size:10px;color:var(--faint);text-transform:uppercase;letter-spacing:.03em}
  .rc-v{font-size:19px;font-weight:600;color:var(--teal)}
  /* Header */
  .head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;
    border:1px solid var(--border);border-radius:12px;padding:18px 22px;border-left:4px solid var(--teal);background:#fff}
  .brand{font-family:'Space Grotesk';font-size:11px;font-weight:700;letter-spacing:.14em;color:var(--teal)}
  .head h1{font-size:22px;font-weight:700;margin:4px 0 2px}
  .head .meta{font-size:12px;color:var(--faint)}
  .opp{text-align:right}.opp .l{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--faint);margin-bottom:5px}
  .opp .v{font-family:'Space Grotesk';font-size:15px;font-weight:700;padding:5px 16px;border-radius:8px}
  /* Hero */
  .hero{display:grid;grid-template-columns:180px 1fr;gap:20px;align-items:center}
  .gauge{width:160px;height:160px;display:block;margin:0 auto}
  .gauge-num{font-size:38px;font-weight:700;fill:var(--ink)}.gauge-pct{font-size:18px;fill:var(--muted)}
  .gauge-cap{font-size:11px;fill:var(--faint);font-family:'IBM Plex Sans'}
  .kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
  .kpi{border:1px solid var(--border);border-radius:10px;padding:12px 14px;background:#fff}
  .kpi-v{font-size:22px;font-weight:700;line-height:1.1}
  .kpi-l{font-size:11px;color:var(--muted);margin-top:3px}
  /* Two-col */
  .two{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .donut-wrap{display:flex;gap:16px;align-items:center;flex-wrap:wrap}
  .donut{width:140px;height:140px;flex-shrink:0}
  .donut-num{font-family:'Space Grotesk';font-size:16px;font-weight:700;fill:var(--ink)}
  .donut-cap{font-size:9px;fill:var(--faint);font-family:'IBM Plex Sans'}
  .legend{flex:1;min-width:170px;display:flex;flex-direction:column;gap:5px}
  .leg-row{display:flex;align-items:center;gap:8px;font-size:12px}
  .leg-dot{width:9px;height:9px;border-radius:3px;flex-shrink:0}
  .leg-name{flex:1;color:var(--ink)}.leg-pct{font-family:'IBM Plex Mono';color:var(--muted);width:48px;text-align:right}
  .leg-amt{font-family:'IBM Plex Mono';color:var(--ink);width:80px;text-align:right;font-weight:500}
  /* h-bars */
  .hbar{margin-bottom:11px}.hbar:last-child{margin-bottom:0}
  .hbar-top{display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px;gap:10px}
  .hbar-top .mono{font-weight:600}
  .hbar-track{height:9px;background:var(--border);border-radius:6px;overflow:hidden}
  .hbar-fill{height:100%;border-radius:6px}
  .badge-n{font-family:'IBM Plex Mono';font-size:10px;color:var(--muted);margin-right:4px}
  .err-p{font-size:11px;max-width:76%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block}
  /* tables */
  .tbl-wrap{overflow-x:auto}
  .tbl{width:100%;border-collapse:collapse;font-size:12.5px}
  .tbl th{text-align:left;font-weight:600;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.03em;padding:8px 10px;border-bottom:1px solid var(--border);background:var(--bg-subtle);white-space:nowrap}
  .tbl td{padding:9px 10px;border-bottom:1px solid var(--border);vertical-align:middle}
  .tbl tr:last-child td{border-bottom:none}
  .svc{font-weight:600;font-size:11px;color:var(--ink-2);background:var(--border);border-radius:5px;padding:2px 7px}
  .save{color:var(--green);font-weight:600;white-space:nowrap}
  .tag{font-size:10.5px;font-weight:600;white-space:nowrap;background:none !important;border:none !important;padding:0}
  .cpu-cell{min-width:120px}
  .pbar{display:inline-block;width:70px;height:8px;background:var(--border);border-radius:5px;overflow:hidden;vertical-align:middle;margin-right:7px}
  .pbar-fill{height:100%;border-radius:5px}.pbar-v{font-size:11px;color:var(--muted)}
  /* logs */
  .log-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px}
  .log-stats>div{border:1px solid var(--border);border-radius:8px;padding:8px 10px;display:flex;flex-direction:column;gap:2px}
  .ls-l{font-size:10px;color:var(--faint);text-transform:uppercase;letter-spacing:.04em}.ls-v{font-size:17px;font-weight:600}
  .err-bars{margin-bottom:14px}
  .term{background:#0B1220;border-radius:10px;overflow:hidden;border:1px solid var(--ink)}
  .term-bar{display:flex;align-items:center;gap:6px;padding:8px 12px;background:#111C2E;border-bottom:1px solid var(--ink)}
  .term-bar .dot{width:10px;height:10px;border-radius:50%}.dot.r{background:#FF5F56}.dot.y{background:#FFBD2E}.dot.g{background:#27C93F}
  .term-title{font-family:'IBM Plex Mono';font-size:11px;color:var(--ink-2);margin-left:8px}
  .term-body{padding:12px 14px;font-family:'IBM Plex Mono';font-size:12px;color:var(--ink-3);line-height:1.7;max-height:none}
  .term-body .ln{white-space:pre-wrap;word-break:break-word}.term-body .pr{color:var(--teal)}
  /* findings / prios */
  .find-card{border-left:4px solid var(--coral)}
  .find-list{list-style:none;display:flex;flex-direction:column;gap:8px}
  .find-list li{display:flex;gap:9px;font-size:13px;color:var(--ink-2);align-items:flex-start}
  .find-mark{width:7px;height:7px;border-radius:50%;background:var(--coral);margin-top:6px;flex-shrink:0}
  .prio-list{display:flex;flex-direction:column;gap:8px}
  .prio{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border);font-size:13px}
  .prio:last-child{border-bottom:none}
  .prio-n{width:22px;height:22px;flex-shrink:0;border-radius:6px;background:var(--border);font-family:'IBM Plex Mono';font-weight:600;font-size:12px;display:flex;align-items:center;justify-content:center;color:var(--muted)}
  .prio-a{flex:1;color:var(--ink-2)}.prio-s{color:var(--green);font-weight:600;white-space:nowrap}
  .intro .lede{font-size:14.5px;line-height:1.7;color:var(--ink-2)}
  /* details / Leer más */
  .more{margin-top:14px}
  .more summary{list-style:none;display:inline-flex;align-items:center;gap:7px;cursor:pointer;
    font-size:12px;font-weight:600;color:var(--teal);background:#F0FDFA;border:1px solid #99F6E4;
    border-radius:999px;padding:5px 14px;user-select:none;transition:background .15s}
  .more summary:hover{background:#CCFBF1}
  .more summary::-webkit-details-marker{display:none}
  .more .chev{transition:transform .2s}
  .more[open] .chev{transform:rotate(180deg)}
  .more .c{display:none}.more[open] .o{display:none}.more[open] .c{display:inline}
  .more-body{margin-top:12px;font-size:13px;line-height:1.7;color:var(--ink-2)}
  .more-body p{margin-bottom:9px}.more-body .just{color:var(--muted)}
  .more-body ul{margin:4px 0 0 18px}.more-body li{margin-bottom:5px}
  /* footer */
  .foot{background:linear-gradient(90deg,#F0FDFA,#ECFDF5);border:1px solid #99F6E4;border-radius:12px;padding:18px 22px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px}
  .foot .fl{font-size:12px;color:var(--muted)}
  .foot .fv{font-family:'Space Grotesk';font-size:26px;font-weight:700;color:var(--teal)}
  .foot .fa{font-family:'IBM Plex Mono';font-size:13px;color:var(--muted)}
  .credit{text-align:center;font-size:10px;color:var(--faint);padding:4px 0 8px}
  @media (max-width:640px){
    .hero{grid-template-columns:1fr}.two{grid-template-columns:1fr}
    .kpis{grid-template-columns:repeat(2,1fr)}.log-stats{grid-template-columns:repeat(2,1fr)}
    .head h1{font-size:19px}.card{background-size:22px 22px}
  }
  @media print{body{padding:0}.card{box-shadow:none}.more>summary{display:none}.more-body{margin-top:0}.more:not([open]) .more-body{display:block}}
</style>${darkInject()}</head>
<body><div class="wrap">
  <div class="head">
    <div>
      <div class="brand">NODWARD · OPTIMIZACIÓN AWS</div>
      <h1>Optimización AWS${account.environment ? ' · ' + dEsc(account.environment) : ''}</h1>
      <div class="meta">${dEsc(account.name || '')}${account.region ? ' · ' + dEsc(account.region) : ''}${genDate ? ' · ' + dEsc(genDate) : ''}</div>
    </div>
    <div class="opp"><div class="l">Oportunidad de ahorro</div>
      <div class="v" style="color:${opp.c};background:${opp.bg}">${dEsc(a.oportunidad_ahorro || '—')}</div></div>
  </div>

  <div class="card"><div class="hero">
    <div>${gauge}</div>
    <div class="kpis">${kpis}</div>
  </div></div>

  ${resumen}
  ${findBlock}

  <div class="two">
    <div class="card"><div class="card-h">Gasto real por servicio</div>${donutBlock}</div>
    <div class="card"><div class="card-h">Ahorro potencial por categoría</div>${catBars}</div>
  </div>

  ${snapBlock}
  ${idleBlock}
  ${schedBlock}
  ${ebsBlock}
  ${logsBlock}
  ${prioBlock}
  ${detMore}

  <div class="foot">
    <div><div class="fl">Ahorro potencial total estimado</div><div class="fv">${dUsd0(ahorroMes)}/mes</div></div>
    <div class="fa">~ ${dUsd0(ahorroAnual)}/año · montos on-demand us-east-1</div>
  </div>
  <div class="credit">Generado por NODWARD AI${report?.generated_by_name ? ' · ' + dEsc(report.generated_by_name) : ''}</div>
</div>
<script>
  (function(){
    function fit(){ try{ if(window.frameElement){ window.frameElement.style.height = (document.documentElement.scrollHeight + 8) + 'px'; } }catch(e){} }
    window.addEventListener('load', fit);
    window.addEventListener('resize', fit);
    document.addEventListener('toggle', function(){ setTimeout(fit, 60); }, true);
    setTimeout(fit, 300); setTimeout(fit, 1200);
  })();
</script>
</body></html>`;
}

function DetalleOptimizacionAws({ a, raw, report }) {
  const html = useMemo(() => buildAwsOptimizationDashboard(a, raw, report), [a, raw, report]);
  return (
    <iframe id="aws-dashboard-frame" title="Dashboard de optimización AWS" srcDoc={html}
      style={{ width: '100%', border: 'none', minHeight: 640, display: 'block' }} />
  );
}

// ── Dashboard visual de Seguridad AWS (mismo diseño que Optimización) ─────────
const RISK_DASH = { 'CRÍTICO': 'var(--red)', 'CRITICO': 'var(--red)', 'ALTO': 'var(--amber)', 'MEDIO': 'var(--accent)', 'BAJO': 'var(--green)' };
const GRADE_DASH = { A: 'var(--green)', B: 'var(--amber)', C: 'var(--amber)', D: 'var(--amber)', F: 'var(--red)' };

function buildAwsSecurityDashboard(a, raw, report) {
  a = a || {}; raw = raw || {};
  const account = raw.account || {};
  const iam = raw.iam || { available: false };
  const findings = raw.findings || [];
  const counts = raw.counts || { critical: 0, medium: 0, info: 0 };
  const audit = raw.audit || null;
  const score = raw.score != null ? raw.score : (a.score != null ? a.score : null);
  const grade = raw.grade || a.grade || '—';
  const gColor = GRADE_DASH[grade] || 'var(--ink-2)';
  const risk = String(a.nivel_riesgo || '').toUpperCase();
  const rColor = RISK_DASH[risk] || 'var(--ink-2)';

  const adminRoles = (iam.roles || []).filter(r => r.adminLike).length;

  // Gauge del score
  const R = 64, C = 2 * Math.PI * R, dash = C * (score || 0) / 100;
  const gauge = `
    <svg viewBox="0 0 160 160" class="gauge">
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="#EEF2F7" stroke-width="14"/>
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="${gColor}" stroke-width="14" stroke-linecap="round"
        stroke-dasharray="${dash.toFixed(1)} ${(C - dash).toFixed(1)}" transform="rotate(-90 80 80)"/>
      <text x="80" y="72" text-anchor="middle" class="gauge-num">${score != null ? score : '—'}</text>
      <text x="80" y="94" text-anchor="middle" class="gauge-grade" fill="${gColor}">Grade ${dEsc(grade)}</text>
    </svg>`;

  const kpis = [
    { v: score != null ? `${score}/100` : '—', l: 'Security Score', c: gColor },
    { v: dInt(counts.critical), l: 'Hallazgos críticos', c: 'var(--coral)' },
    { v: dInt(counts.medium), l: 'Hallazgos medios', c: 'var(--amber)' },
    { v: dInt(adminRoles), l: 'Roles admin / *:*', c: 'var(--violet)' },
    { v: iam.available ? dInt(iam.account.usersNoMfa || 0) : '—', l: 'Usuarios sin MFA', c: (iam.account?.usersNoMfa) ? 'var(--coral)' : 'var(--green)' },
    { v: audit ? dInt(audit.summary.root || 0) : '—', l: 'Uso de root (7d)', c: (audit?.summary.root) ? 'var(--coral)' : 'var(--faint)' },
  ].map(k => `<div class="kpi"><div class="kpi-v" style="color:${k.c}">${k.v}</div><div class="kpi-l">${k.l}</div></div>`).join('');

  const resumen = a.resumen_ejecutivo ? `<div class="card intro"><div class="card-h">Resumen ejecutivo</div><p class="lede">${dEsc(a.resumen_ejecutivo)}</p></div>` : '';
  const findsCrit = (a.hallazgos_criticos || []).filter(Boolean);
  const findBlock = findsCrit.length === 0 ? '' : `
    <div class="card find-card"><div class="card-h">Hallazgos críticos</div>
      <ul class="find-list">${findsCrit.map(f => `<li><span class="find-mark"></span>${dEsc(f)}</li>`).join('')}</ul></div>`;

  // Postura IAM (chips)
  const chip = (ok, label) => `<span class="chip ${ok ? 'ok' : 'bad'}">${ok ? '✓' : '✗'} ${dEsc(label)}</span>`;
  const iamBlock = !iam.available ? '' : `
    <div class="card"><div class="card-h">IAM — Postura de cuenta</div>
      <div class="chips">
        ${chip(iam.account.rootMfa === true, 'Root con MFA')}
        ${chip(iam.account.rootKeys === false, 'Root sin access keys')}
        ${chip(iam.account.passwordPolicy === true, 'Password policy')}
        ${chip((iam.account.usersNoMfa || 0) === 0, `Usuarios sin MFA: ${iam.account.usersNoMfa ?? '—'}`)}
        ${chip((iam.account.oldKeys || 0) === 0, `Keys >90d: ${iam.account.oldKeys ?? '—'}`)}
      </div>
      ${dashMore(a.iam)}
    </div>`;

  // Roles y dónde se usan
  const rolesBlock = !iam.available || !(iam.roles || []).length ? '' : `
    <div class="card"><div class="card-h">Roles y dónde se usan <span class="count">${iam.roles.length}${iam.totalRoles ? ' de ' + iam.totalRoles : ''}</span></div>
      <div class="tbl-wrap"><table class="tbl">
        <thead><tr><th>Rol</th><th>Usado por</th><th>Último uso</th><th>Privilegio</th></tr></thead>
        <tbody>${iam.roles.map(r => `<tr>
          <td class="mono">${dEsc(r.name)}</td>
          <td>${r.usedBy.length ? dEsc(r.usedBy.slice(0, 4).join(', ')) + (r.usedBy.length > 4 ? ` +${r.usedBy.length - 4}` : '') : '<span class="muted">sin uso</span>'}</td>
          <td class="muted">${r.lastUsed ? new Date(r.lastUsed).toLocaleDateString('es-AR') : '—'}</td>
          <td>${r.adminLike ? '<span class="tag" style="color:#92400e;border-color:var(--amber-dim);background:#fff7ed">admin / *:*</span>' : '<span class="muted">—</span>'}</td>
        </tr>`).join('')}</tbody>
      </table></div>
    </div>`;

  // Exposición — hallazgos por severidad
  const SEV = [{ k: 'critical', c: 'var(--red)', l: 'Críticos' }, { k: 'medium', c: 'var(--amber)', l: 'Medios' }, { k: 'info', c: 'var(--accent)', l: 'Informativos' }];
  const findingsHtml = SEV.map(sv => {
    const list = findings.filter(f => f.severity === sv.k);
    if (!list.length) return '';
    return `<div class="card-sub" style="margin-top:10px;color:${sv.c};font-weight:700">${sv.l} (${list.length})</div>` +
      list.map(f => `<div class="sev" style="border-left-color:${sv.c}">
        <div class="sev-h"><span class="sev-svc" style="color:${sv.c}">${dEsc(f.service)}</span> ${dEsc(f.issue)}</div>
        <div class="sev-d"><b>${dEsc(f.resource)}</b> — ${dEsc(f.detail)}</div>
      </div>`).join('');
  }).join('');
  const exposBlock = findings.length === 0 ? '' : `
    <div class="card"><div class="card-h">Exposición y hallazgos <span class="count">${findings.length}</span></div>
      ${findingsHtml}
      ${dashMore(a.exposicion)}
    </div>`;

  // Auditoría (CloudTrail)
  const auditBlock = !audit ? '' : `
    <div class="card"><div class="card-h">Auditoría — CloudTrail (últimas ${audit.hours}h)</div>
      <div class="log-stats" style="grid-template-columns:repeat(4,1fr)">
        ${[
          ['Uso de root', audit.summary.root, 'var(--red)'], ['Logins fallidos', audit.summary.login_fallido, 'var(--amber)'],
          ['Destructivos', audit.summary.destructivo, 'var(--red)'], ['Anti-auditoría', audit.summary.anti_auditoria, 'var(--red)'],
          ['Cambios IAM', audit.summary.iam, 'var(--amber)'], ['Cambios red/SG', audit.summary.red, 'var(--amber)'],
          ['Login sin MFA', audit.summary.login_sin_mfa, 'var(--amber)'], ['Total notables', audit.summary.total, 'var(--ink)'],
        ].map(([l, v, c]) => `<div><span class="ls-l">${l}</span><span class="ls-v mono" style="color:${(v || 0) > 0 ? c : 'var(--ink-3)'}">${dInt(v || 0)}</span></div>`).join('')}
      </div>
      ${dashMore(a.auditoria)}
    </div>`;

  // Prioridades
  const IMP = { ALTO: 'var(--coral)', MEDIO: 'var(--amber)', BAJO: 'var(--green)' };
  const prios = a.prioridades || [];
  const prioBlock = prios.length === 0 ? '' : `
    <div class="card"><div class="card-h">Plan de remediación priorizado</div>
      <div class="prio-list">${prios.map(p => `<div class="prio"><span class="prio-n">${dEsc(p.orden)}</span>
        <span class="prio-a">${dEsc(p.accion)}</span>
        <span class="tag" style="color:${IMP[String(p.impacto || '').toUpperCase()] || 'var(--muted)'};border-color:${IMP[String(p.impacto || '').toUpperCase()] || 'var(--muted)'}40">${dEsc(p.impacto || '')}</span></div>`).join('')}</div>
    </div>`;

  const detMore = a.analisis_detallado ? `<div class="card"><div class="card-h">Análisis técnico detallado</div>${dashMore({ analisis: a.analisis_detallado })}</div>` : '';
  const genDate = report?.created_at ? new Date(report.created_at).toLocaleString('es-AR') : '';

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
<style>${DASH_STYLE}</style>${darkInject()}</head>
<body><div class="wrap">
  <div class="head" style="border-left-color:${gColor}">
    <div>
      <div class="brand" style="color:${gColor}">NODWARD · SEGURIDAD AWS</div>
      <h1>Seguridad AWS${account.environment ? ' · ' + dEsc(account.environment) : ''}</h1>
      <div class="meta">${dEsc(account.name || '')}${account.region ? ' · ' + dEsc(account.region) : ''}${genDate ? ' · ' + dEsc(genDate) : ''}</div>
    </div>
    <div class="opp"><div class="l">Nivel de riesgo</div>
      <div class="v" style="color:${rColor};background:${rColor}0f">${dEsc(a.nivel_riesgo || '—')}</div></div>
  </div>

  <div class="card"><div class="hero"><div>${gauge}</div><div class="kpis">${kpis}</div></div></div>

  ${resumen}
  ${findBlock}
  ${iamBlock}
  ${rolesBlock}
  ${exposBlock}
  ${auditBlock}
  ${prioBlock}
  ${detMore}

  <div class="foot" style="background:linear-gradient(90deg,${gColor}0f,${gColor}05);border-color:${gColor}55">
    <div><div class="fl">Security Score del ambiente</div><div class="fv" style="color:${gColor}">${score != null ? score + '/100' : '—'} · ${dEsc(grade)}</div></div>
    <div class="fa">Nivel de riesgo: ${dEsc(a.nivel_riesgo || '—')}</div>
  </div>
  <div class="credit">Generado por NODWARD AI${report?.generated_by_name ? ' · ' + dEsc(report.generated_by_name) : ''}</div>
</div>
<script>
  (function(){
    function fit(){ try{ if(window.frameElement){ window.frameElement.style.height = (document.documentElement.scrollHeight + 8) + 'px'; } }catch(e){} }
    window.addEventListener('load', fit); window.addEventListener('resize', fit);
    document.addEventListener('toggle', function(){ setTimeout(fit, 60); }, true);
    setTimeout(fit, 300); setTimeout(fit, 1200);
  })();
</script>
</body></html>`;
}

function DetalleSeguridadAws({ a, raw, report }) {
  const html = useMemo(() => buildAwsSecurityDashboard(a, raw, report), [a, raw, report]);
  return (
    <iframe id="aws-dashboard-frame" title="Dashboard de seguridad AWS" srcDoc={html}
      style={{ width: '100%', border: 'none', minHeight: 640, display: 'block' }} />
  );
}

// ── Dashboard visual de Plan de Disaster Recovery (mismo diseño) ──────────────
const TIER_C = { critica: 'var(--red)', alta: 'var(--amber)', media: 'var(--accent)', baja: 'var(--green)' };
const TIER_L = { critica: 'Crítica', alta: 'Alta', media: 'Media', baja: 'Baja' };

function buildDrDashboard(a, raw, report) {
  a = a || {}; raw = raw || {};
  const cov = a.cobertura_pct != null ? a.cobertura_pct : (raw.coverage ? raw.coverage.pct : null);
  const covColor = cov == null ? 'var(--ink-2)' : cov >= 80 ? 'var(--green)' : cov >= 50 ? 'var(--amber)' : 'var(--red)';
  const risk = String(a.nivel_riesgo || '').toUpperCase();
  const rColor = RISK_DASH[risk] || 'var(--ink-2)';
  const tiers = raw.tiers || [];
  const gaps = raw.gaps || [];
  const byTier = raw.byTier || {};
  const tier1 = tiers.find(t => t.criticidad === 'critica');

  const R = 64, C = 2 * Math.PI * R, dash = C * (cov || 0) / 100;
  const gauge = `
    <svg viewBox="0 0 160 160" class="gauge">
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="#EEF2F7" stroke-width="14"/>
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="${covColor}" stroke-width="14" stroke-linecap="round"
        stroke-dasharray="${dash.toFixed(1)} ${(C - dash).toFixed(1)}" transform="rotate(-90 80 80)"/>
      <text x="80" y="74" text-anchor="middle" class="gauge-num">${cov != null ? cov : '—'}<tspan class="gauge-pct">%</tspan></text>
      <text x="80" y="98" text-anchor="middle" class="gauge-cap">cobertura DR</text>
    </svg>`;

  const kpis = [
    { v: cov != null ? `${cov}%` : '—', l: 'Cobertura DR (crít./altos con plan)', c: covColor },
    { v: dInt(byTier.critica || 0), l: 'Activos críticos', c: 'var(--coral)' },
    { v: dInt(byTier.alta || 0), l: 'Activos altos', c: 'var(--amber)' },
    { v: dInt(gaps.length), l: 'Gaps (sin DRP / sin HA)', c: gaps.length ? 'var(--coral)' : 'var(--green)' },
    { v: dInt(raw.total || 0), l: 'Activos clasificados', c: 'var(--ink)' },
    { v: tier1 && tier1.minRto != null ? `${tier1.minRto}h` : '—', l: 'RTO más exigente', c: 'var(--blue)' },
  ].map(k => `<div class="kpi"><div class="kpi-v" style="color:${k.c}">${k.v}</div><div class="kpi-l">${k.l}</div></div>`).join('');

  const resumen = a.resumen_ejecutivo ? `<div class="card intro"><div class="card-h">Resumen ejecutivo</div><p class="lede">${dEsc(a.resumen_ejecutivo)}</p></div>` : '';
  const findsCrit = (a.hallazgos_criticos || []).filter(Boolean);
  const findBlock = findsCrit.length === 0 ? '' : `
    <div class="card find-card"><div class="card-h">Hallazgos críticos</div>
      <ul class="find-list">${findsCrit.map(f => `<li><span class="find-mark"></span>${dEsc(f)}</li>`).join('')}</ul></div>`;

  // Tiers de recuperación
  const chipYN = (ok, y, n) => `<span class="tag" style="color:${ok ? 'var(--green)' : 'var(--red)'};border-color:${ok ? 'var(--green-dim)' : 'var(--red-dim)'};background:${ok ? 'var(--green-dim)' : 'var(--red-dim)'}">${ok ? y : n}</span>`;
  const tiersBlock = tiers.length === 0 ? '' : `
    <div class="card"><div class="card-h">Tiers de recuperación</div>
      ${tiers.map(t => `
        <div class="card-sub" style="margin-top:12px;color:${TIER_C[t.criticidad]};font-weight:700">
          ${TIER_L[t.criticidad] || t.criticidad} · ${t.count} activo(s)${t.minRto != null ? ` · RTO ${t.minRto}${t.maxRto !== t.minRto ? '–' + t.maxRto : ''}h` : ''}
        </div>
        <div class="tbl-wrap"><table class="tbl">
          <thead><tr><th>Activo</th><th>RTO</th><th>Plan DR</th><th>Alta disp.</th><th>Ambiente</th></tr></thead>
          <tbody>${t.assets.map(x => `<tr>
            <td class="mono">${dEsc(x.hostname)}</td>
            <td class="mono">${x.rto_horas != null ? x.rto_horas + 'h' : '—'}</td>
            <td>${chipYN(x.drp, 'Sí', 'No')}</td>
            <td>${chipYN(x.ha, 'Sí', 'No')}</td>
            <td class="muted">${dEsc(x.ambiente || '—')}</td>
          </tr>`).join('')}</tbody>
        </table></div>`).join('')}
      ${dashMore(a.tiers)}
    </div>`;

  // Gaps
  const gapsBlock = gaps.length === 0 ? '' : `
    <div class="card"><div class="card-h">Gaps de continuidad <span class="count">${gaps.length}</span></div>
      <div class="card-sub">Activos críticos/altos sin plan de DR o sin alta disponibilidad.</div>
      <div class="tbl-wrap"><table class="tbl">
        <thead><tr><th>Activo</th><th>Criticidad</th><th>RTO</th><th>Falta</th><th>Impacto</th></tr></thead>
        <tbody>${gaps.map(g => `<tr>
          <td class="mono">${dEsc(g.hostname)}</td>
          <td><span class="tag" style="color:${TIER_C[g.criticidad]};border-color:${TIER_C[g.criticidad]}40">${TIER_L[g.criticidad] || g.criticidad}</span></td>
          <td class="mono">${g.rto_horas != null ? g.rto_horas + 'h' : '—'}</td>
          <td>${[g.sinDrp ? 'plan DR' : '', g.sinHa ? 'alta disp.' : ''].filter(Boolean).join(' + ')}</td>
          <td class="muted">${dEsc(g.impacto || '—')}</td>
        </tr>`).join('')}</tbody>
      </table></div>
      ${dashMore(a.gaps)}
    </div>`;

  // Checklist de recuperación
  const checklist = a.checklist_recuperacion || [];
  const checkBlock = checklist.length === 0 ? '' : `
    <div class="card"><div class="card-h">Checklist de recuperación</div>
      <div class="prio-list">${checklist.map((step, i) => `<div class="prio"><span class="prio-n">${i + 1}</span><span class="prio-a">${dEsc(step)}</span></div>`).join('')}</div>
    </div>`;

  const IMP = { ALTO: 'var(--coral)', MEDIO: 'var(--amber)', BAJO: 'var(--green)' };
  const prios = a.prioridades || [];
  const prioBlock = prios.length === 0 ? '' : `
    <div class="card"><div class="card-h">Plan de acción priorizado</div>
      <div class="prio-list">${prios.map(p => `<div class="prio"><span class="prio-n">${dEsc(p.orden)}</span>
        <span class="prio-a">${dEsc(p.accion)}</span>
        <span class="tag" style="color:${IMP[String(p.impacto || '').toUpperCase()] || 'var(--muted)'};border-color:${IMP[String(p.impacto || '').toUpperCase()] || 'var(--muted)'}40">${dEsc(p.impacto || '')}</span></div>`).join('')}</div>
    </div>`;

  const detMore = a.analisis_detallado ? `<div class="card"><div class="card-h">Análisis técnico detallado</div>${dashMore({ analisis: a.analisis_detallado })}</div>` : '';
  const genDate = report?.created_at ? new Date(report.created_at).toLocaleString('es-AR') : '';

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
<style>${DASH_STYLE}</style>${darkInject()}</head>
<body><div class="wrap">
  <div class="head" style="border-left-color:${covColor}">
    <div>
      <div class="brand" style="color:${covColor}">NODWARD · DISASTER RECOVERY</div>
      <h1>Plan de DR${raw.location ? ' · ' + dEsc(raw.location) : ''}</h1>
      <div class="meta">Basado en la matriz de criticidad${genDate ? ' · ' + dEsc(genDate) : ''}</div>
    </div>
    <div class="opp"><div class="l">Riesgo de continuidad</div>
      <div class="v" style="color:${rColor};background:${rColor}0f">${dEsc(a.nivel_riesgo || '—')}</div></div>
  </div>

  <div class="card"><div class="hero"><div>${gauge}</div><div class="kpis">${kpis}</div></div></div>

  ${resumen}
  ${findBlock}
  ${tiersBlock}
  ${gapsBlock}
  ${checkBlock}
  ${prioBlock}
  ${detMore}

  <div class="foot" style="background:linear-gradient(90deg,${covColor}0f,${covColor}05);border-color:${covColor}55">
    <div><div class="fl">Cobertura de DR (activos críticos/altos con plan)</div><div class="fv" style="color:${covColor}">${cov != null ? cov + '%' : '—'}</div></div>
    <div class="fa">${dInt(gaps.length)} gap(s) · riesgo ${dEsc(a.nivel_riesgo || '—')}</div>
  </div>
  <div class="credit">Generado por NODWARD AI${report?.generated_by_name ? ' · ' + dEsc(report.generated_by_name) : ''}</div>
</div>
<script>
  (function(){
    function fit(){ try{ if(window.frameElement){ window.frameElement.style.height = (document.documentElement.scrollHeight + 8) + 'px'; } }catch(e){} }
    window.addEventListener('load', fit); window.addEventListener('resize', fit);
    document.addEventListener('toggle', function(){ setTimeout(fit, 60); }, true);
    setTimeout(fit, 300); setTimeout(fit, 1200);
  })();
</script>
</body></html>`;
}

function DetalleDrPlan({ a, raw, report }) {
  const html = useMemo(() => buildDrDashboard(a, raw, report), [a, raw, report]);
  return (
    <iframe id="aws-dashboard-frame" title="Dashboard de plan de DR" srcDoc={html}
      style={{ width: '100%', border: 'none', minHeight: 640, display: 'block' }} />
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DASHBOARD INFOGRÁFICO ESTÁNDAR (genérico para cualquier tipo de informe)
// Consume el objeto `analysis.infografia` que emite la IA. Dos variantes de render:
//   'basico'    → 1 carilla: gauge + kpis + donas + hallazgos clave + prioritarios
//   'detallado' → completo tipo CDM: + tarjetas de hallazgo (Riesgo/Impacto/Remediación)
//                 + tabla de esfuerzo (ROI) + matriz de capacidades
// ══════════════════════════════════════════════════════════════════════════════
// Colores HEX concretos (NO var()): dentro del iframe las CSS vars de la app no cascadean y
// var() en atributos SVG stroke= no es confiable → los tonos deben ser hex directos.
const STD_TONE = { bad:'#DC2626', critico:'#DC2626', 'crítico':'#DC2626', alto:'#DC2626',
  warn:'#D97706', atencion:'#D97706', 'atención':'#D97706', medio:'#D97706',
  good:'#16A34A', ok:'#16A34A', bueno:'#16A34A', bajo:'#16A34A', info:'#0284C7', neutral:'#94A3B8' };
function stdTone(tone, i) { const t = String(tone || '').toLowerCase(); return STD_TONE[t] || DASH_PAL[(i || 0) % DASH_PAL.length]; }

const STD_STYLE = `
  :root{--coral:#DC2626;--red:#DC2626;--amber:#D97706;--green:#16A34A;--blue:#0284C7;--violet:#7C3AED;--teal:#0D9488;--ink:#0F172A;--ink-2:#334155;--ink-3:#64748B;--muted:#64748B;--faint:#94A3B8;--bg-subtle:#F1F5F9;}
  .keybox{list-style:none;display:flex;flex-direction:column;gap:7px}
  .keybox li{display:flex;gap:9px;font-size:13px;color:var(--ink-2);align-items:flex-start}
  .keybox .k-mark{width:7px;height:7px;border-radius:50%;background:var(--amber);margin-top:6px;flex-shrink:0}
  .dtrack{stroke:var(--border)}
  .fcard{border:1px solid var(--border);border-radius:12px;overflow:hidden;background:var(--panel)}
  .fcard-bar{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:10px 16px;color:#fff}
  .fcard-bar b{font-family:'Space Grotesk';font-size:14px;font-weight:600}
  .fcard-sev{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;opacity:.95;white-space:nowrap}
  .fcard-body{display:grid;grid-template-columns:1fr 150px;gap:16px;padding:14px 16px;align-items:center}
  .fcard-body.nochart{grid-template-columns:1fr}
  .fcard-txt p{font-size:13px;line-height:1.6;color:var(--ink-2);margin-bottom:7px}
  .fcard-txt p:last-child{margin-bottom:0}
  .fcard-rem{background:rgba(61,220,132,.08);border-top:1px solid rgba(61,220,132,.32);padding:10px 16px;font-size:12.5px;color:var(--ink-2);line-height:1.55}
  .fcard-rem b{color:var(--green)}
  .stack{display:flex;flex-direction:column;gap:10px}
  .mtx{width:100%;border-collapse:collapse;font-size:12.5px}
  .mtx th{text-align:left;background:var(--blue);color:#fff;font-size:11px;text-transform:uppercase;letter-spacing:.03em;padding:8px 10px}
  .mtx td{padding:8px 10px;border-bottom:1px solid var(--border);vertical-align:top}
  .mtx tr:last-child td{border-bottom:none}
  .mtx td:first-child{font-weight:600;color:var(--ink);width:34%}
  .eff td.eff-total,.eff tr.eff-total td{font-weight:700;border-top:2px solid var(--border)}
`;

function stdDonut(segments, opts = {}) {
  segments = (segments || []).filter(s => s && Number(s.value) > 0);
  if (!segments.length) return '';
  const size = opts.size || 130, sw = opts.sw || 20;
  const r = size / 2 - sw / 2 - 2, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0) || 1;
  let acc = 0;
  const arcs = segments.map((s, i) => {
    const frac = (Number(s.value) || 0) / total, dash = C * frac, rot = -90 + acc * 360;
    acc += frac;
    return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${stdTone(s.tone, i)}" stroke-width="${sw}" stroke-linecap="butt" stroke-dasharray="${dash.toFixed(2)} ${(C - dash).toFixed(2)}" transform="rotate(${rot.toFixed(2)} ${cx} ${cy})"/>`;
  }).join('');
  const center = opts.center != null ? `<text x="${cx}" y="${cy + 5}" text-anchor="middle" class="donut-num">${dEsc(opts.center)}</text>` : '';
  const svg = `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" class="donut"><circle class="dtrack" cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke-width="${sw}"/>${arcs}${center}</svg>`;
  const legend = opts.legend === false ? '' : `<div class="legend">${segments.map((s, i) => `<div class="leg-row"><span class="leg-dot" style="background:${stdTone(s.tone, i)}"></span><span class="leg-name">${dEsc(s.label)}</span><span class="leg-pct">${dInt(s.value)}</span></div>`).join('')}</div>`;
  return `<div class="donut-wrap">${svg}${legend}</div>`;
}

function stdGaugeColor(inf, a) {
  const est = String((inf.estado != null ? inf.estado : a.nivel_riesgo) || '').toUpperCase();
  if (['CRÍTICO', 'CRITICO', 'ALTO'].includes(est)) return '#DC2626';
  if (['MEDIO', 'ATENCIÓN', 'ATENCION'].includes(est)) return '#D97706';
  if (['BAJO', 'OK', 'BUENO'].includes(est)) return '#16A34A';
  const g = inf.gauge || {}, v = Number(g.value), mx = Number(g.max) || 100;
  if (!isNaN(v)) { const p = v / mx * 100; return p >= 75 ? '#16A34A' : p >= 50 ? '#D97706' : '#DC2626'; }
  return '#0D9488';
}

// Fallback: si un informe viejo no trae `infografia`, sintetizar lo mínimo desde las claves comunes.
function stdEnvelope(a) {
  if (a.infografia && typeof a.infografia === 'object') return a.infografia;
  return {
    estado: a.nivel_riesgo, gauge: null, kpis: [], donuts: [],
    hallazgos_clave: (a.hallazgos_criticos || []).slice(0, 6),
    prioritarios: null, hallazgos: [], esfuerzo: null, matriz: null,
  };
}

function buildStandardDashboard(a, raw, report, variant = 'detallado') {
  a = a || {};
  const inf = stdEnvelope(a);
  const detailed = variant !== 'basico';
  const gColor = stdGaugeColor(inf, a);
  const type = (REPORT_TYPES.find(t => t.id === (report?.report_type || 'ejecutivo')) || {});
  const estado = inf.estado != null ? inf.estado : (a.nivel_riesgo || '—');

  // Gauge
  let gauge = '';
  if (inf.gauge && inf.gauge.value != null) {
    const g = inf.gauge, mx = Number(g.max) || 100, R = 64, C = 2 * Math.PI * R, dash = C * Math.min(1, (Number(g.value) || 0) / mx);
    gauge = `<svg viewBox="0 0 160 160" class="gauge">
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="#EEF2F7" stroke-width="14"/>
      <circle cx="80" cy="80" r="${R}" fill="none" stroke="${gColor}" stroke-width="14" stroke-linecap="round" stroke-dasharray="${dash.toFixed(1)} ${(C - dash).toFixed(1)}" transform="rotate(-90 80 80)"/>
      <text x="80" y="74" text-anchor="middle" class="gauge-num">${dEsc(g.value)}</text>
      <text x="80" y="96" text-anchor="middle" class="gauge-cap">${dEsc(g.label || (g.max ? 'de ' + g.max : ''))}</text>
    </svg>${g.grade ? `<div style="text-align:center;margin-top:2px;font-weight:700;color:${gColor};font-family:'Space Grotesk';font-size:14px">${dEsc(g.grade)}</div>` : ''}`;
  }

  // KPIs
  const kpis = (inf.kpis || []).slice(0, 6).map((k, i) =>
    `<div class="kpi"><div class="kpi-v" style="color:${stdTone(k.tone, i)}">${dEsc(k.value)}</div><div class="kpi-l">${dEsc(k.label)}</div></div>`).join('');
  const hero = (gauge || kpis) ? `<div class="card"><div class="hero" style="${gauge ? '' : 'grid-template-columns:1fr'}">${gauge ? `<div>${gauge}</div>` : ''}${kpis ? `<div class="kpis">${kpis}</div>` : ''}</div></div>` : '';

  // Donas
  const donuts = (inf.donuts || []).filter(d => d && (d.segments || []).length).map(d =>
    `<div class="card"><div class="card-h">${dEsc(d.title || 'Distribución')}</div>${stdDonut(d.segments, { center: d.center })}</div>`).join('');
  const donutsRow = donuts ? (inf.donuts.length >= 2 ? `<div class="two">${donuts}</div>` : donuts) : '';

  // Hallazgos clave
  const keys = (inf.hallazgos_clave || []).filter(Boolean);
  const keyBox = keys.length ? `<div class="card"><div class="card-h">Hallazgos clave</div>
    <ul class="keybox">${keys.map(k => `<li><span class="k-mark"></span>${dEsc(k)}</li>`).join('')}</ul></div>` : '';

  // Resumen ejecutivo
  const resumen = a.resumen_ejecutivo ? `<div class="card intro"><div class="card-h">Resumen ejecutivo</div><p class="lede">${dEsc(a.resumen_ejecutivo)}</p></div>` : '';

  // Tabla de ítems prioritarios
  const genTable = (t, cls = '') => {
    if (!t || !(t.rows || []).length) return '';
    const cols = t.columns || [];
    const body = t.rows.map(r => {
      const cells = Array.isArray(r) ? r : (r.cells || []);
      const tc = (r && r.__total) ? ' class="eff-total"' : '';
      return `<tr>${cells.map(cell => `<td${tc}>${dEsc(cell)}</td>`).join('')}</tr>`;
    }).join('');
    return `<div class="tbl-wrap"><table class="tbl ${cls}"><thead><tr>${cols.map(c => `<th>${dEsc(c)}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table></div>`;
  };
  const prioritarios = inf.prioritarios && (inf.prioritarios.rows || []).length
    ? `<div class="card"><div class="card-h">${dEsc(inf.prioritarios.titulo || 'Ítems prioritarios')}</div>${genTable(inf.prioritarios)}</div>` : '';

  // ── DETALLADO ──
  let findingCards = '', esfuerzo = '', matriz = '', detMore = '';
  if (detailed) {
    const SEVC = { 'CRÍTICO': '#DC2626', 'CRITICO': '#DC2626', 'ALTO': '#D97706', 'MEDIO': '#0284C7', 'BAJO': '#16A34A' };
    findingCards = (inf.hallazgos || []).map((h, i) => {
      const sev = String(h.severidad || 'MEDIO').toUpperCase(), c = SEVC[sev] || '#64748B';
      const chart = h.chart && (h.chart.segments || []).length ? stdDonut(h.chart.segments, { size: 120, sw: 18, legend: true }) : '';
      const body = [
        h.riesgo ? `<p><b>Riesgo:</b> ${dEsc(h.riesgo)}</p>` : '',
        h.impacto ? `<p><b>Impacto:</b> ${dEsc(h.impacto)}</p>` : '',
      ].join('');
      return `<div class="fcard">
        <div class="fcard-bar" style="background:${c}"><b>${i + 1}&nbsp;&nbsp;${dEsc(h.titulo || '')}</b><span class="fcard-sev">${dEsc(sev)}</span></div>
        <div class="fcard-body${chart ? '' : ' nochart'}"><div class="fcard-txt">${body}</div>${chart ? `<div>${chart}</div>` : ''}</div>
        ${h.remediacion ? `<div class="fcard-rem"><b>» Cómo se remedia:</b> ${dEsc(h.remediacion)}</div>` : ''}
      </div>`;
    }).join('');
    if (findingCards) findingCards = `<div class="card-h" style="margin:2px 2px -4px">Detalle de hallazgos</div><div class="stack">${findingCards}</div>`;

    if (inf.esfuerzo && (inf.esfuerzo.rows || []).length) {
      const e = inf.esfuerzo;
      const rows = e.rows.map(r => (Array.isArray(r) ? r : []));
      if (e.total) {
        const totalRow = new Array(Math.max(2, (e.columns || []).length)).fill('');
        totalRow[0] = 'TOTAL'; totalRow[totalRow.length - 1] = e.total; totalRow.__total = true;
        rows.push(totalRow);
      }
      esfuerzo = `<div class="card"><div class="card-h">${dEsc(e.titulo || 'Esfuerzo estimado')}</div>${genTable({ columns: e.columns, rows }, 'eff')}</div>`;
    }
    if (inf.matriz && (inf.matriz.rows || []).length) {
      matriz = `<div class="card"><div class="card-h">${dEsc(inf.matriz.titulo || 'Matriz de capacidades')}</div>
        <div class="tbl-wrap"><table class="mtx"><thead><tr><th>${dEsc(inf.matriz.col_a || 'Desafío')}</th><th>${dEsc(inf.matriz.col_b || 'Cómo lo resuelve la suite')}</th></tr></thead>
        <tbody>${inf.matriz.rows.map(r => `<tr><td>${dEsc(r.desafio || r[0] || '')}</td><td>${dEsc(r.capacidad || r[1] || '')}</td></tr>`).join('')}</tbody></table></div></div>`;
    }
    if (a.analisis_detallado) detMore = `<div class="card"><div class="card-h">Análisis técnico detallado</div>${dashMore({ analisis: a.analisis_detallado })}</div>`;
  }

  const genDate = report?.created_at ? new Date(report.created_at).toLocaleString('es-AR') : '';
  const titulo = inf.titulo || type.label || 'Informe';
  const rColor = stdGaugeColor(inf, a);

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
<style>${DASH_STYLE}${STD_STYLE}</style>${darkInject()}</head>
<body><div class="wrap">
  <div class="head" style="border-left-color:${gColor}">
    <div>
      <div class="brand" style="color:${gColor}">NODWARD · ${dEsc((type.label || 'INFORME').toUpperCase())}</div>
      <h1>${dEsc(titulo)}</h1>
      <div class="meta">${dEsc(report?.period_label || '')}${genDate ? ' · ' + dEsc(genDate) : ''}${inf.alcance ? ' · ' + dEsc(inf.alcance) : ''}</div>
    </div>
    <div class="opp"><div class="l">Estado</div>
      <div class="v" style="color:${rColor};background:${rColor}0f">${dEsc(estado)}</div></div>
  </div>
  ${hero}
  ${donutsRow}
  ${keyBox}
  ${resumen}
  ${prioritarios}
  ${findingCards}
  ${esfuerzo}
  ${matriz}
  ${detMore}
  <div class="credit">Generado por NODWARD AI${report?.generated_by_name ? ' · ' + dEsc(report.generated_by_name) : ''}${genDate ? ' · ' + dEsc(genDate) : ''}</div>
</div>
<script>
  (function(){
    function fit(){ try{ if(window.frameElement){ window.frameElement.style.height = (document.documentElement.scrollHeight + 8) + 'px'; } }catch(e){} }
    window.addEventListener('load', fit); window.addEventListener('resize', fit);
    document.addEventListener('toggle', function(){ setTimeout(fit, 60); }, true);
    setTimeout(fit, 300); setTimeout(fit, 1200);
  })();
</script>
</body></html>`;
}

function DetalleStandard({ a, raw, report, variant }) {
  const html = useMemo(() => buildStandardDashboard(a, raw, report, variant), [a, raw, report, variant]);
  return (
    <iframe id="aws-dashboard-frame" title="Dashboard del informe" srcDoc={html}
      style={{ width: '100%', border: 'none', minHeight: 640, display: 'block' }} />
  );
}

// Tipos que usan el dashboard infográfico estándar (con toggle básico/detallado).
const STD_DASH_TYPES = ['seguridad', 'ejecutivo'];

const DETALLE = {
  optimizacion_aws: DetalleOptimizacionAws,
  seguridad_aws: DetalleSeguridadAws,
  dr_plan: DetalleDrPlan,
  seguridad: DetalleStandard,
  ejecutivo: DetalleStandard,
};

// ── Helpers de UI ─────────────────────────────────────────────────────────────

function RiskBadge({ level }) {
  const c = RISK_CFG[level] || RISK_CFG['MEDIO'];
  return <span style={{ display:'inline-flex', alignItems:'center', fontSize:11, fontWeight:700, color:c.color, letterSpacing:'0.04em' }}>{level}</span>;
}

function Section({ label, icon: Icon, iconColor = 'var(--ink-2)', children }) {
  return (
    <div className="card" style={{ padding:'20px 24px', marginBottom:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <Icon size={14} style={{ color: iconColor }} />
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', color:'var(--ink-3)' }}>{label}</span>
      </div>
      {children}
    </div>
  );
}

function AnalysisSection({ label, icon, iconColor, data }) {
  if (!data) return null;
  return (
    <Section label={label} icon={icon} iconColor={iconColor}>
      <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 10px' }}>{data.analisis}</p>
      {data.recomendaciones?.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          {data.recomendaciones.map((r, i) => (
            <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7 }}>
              <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
              <span style={{ fontSize:12, color:'var(--ink-2)' }}>{r}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Priorities({ items }) {
  if (!items?.length) return null;
  const impColor = { ALTO:'var(--red)', MEDIO:'var(--amber)', BAJO:'var(--green)' };
  return (
    <Section label="PLAN DE ACCIÓN PRIORITARIO" icon={TrendingUp} iconColor="var(--green)">
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {items.map(p => (
          <div key={p.orden} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'10px 14px', background:'var(--bg-subtle)', borderRadius:5, border:'1px solid var(--border)' }}>
            <div style={{ width:24, height:24, borderRadius:'50%', background:'var(--ink)', color:'var(--surface)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0 }}>{p.orden}</div>
            <div style={{ flex:1, fontSize:13, color:'var(--ink-2)', lineHeight:1.5 }}>{p.accion}</div>
            <span style={{ fontSize:10, fontWeight:700, color:impColor[p.impacto]||'var(--ink-2)', flexShrink:0 }}>{p.impacto}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Findings({ items, riskColor }) {
  if (!items?.length) return null;
  return (
    <div className="card" style={{ padding:'20px 24px', marginBottom:12, borderLeft:`4px solid ${riskColor}` }}>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', color:'var(--ink-3)', marginBottom:12 }}>HALLAZGOS CRÍTICOS</div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {items.map((h, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
            <AlertTriangle size={14} style={{ color:riskColor, marginTop:2, flexShrink:0 }} />
            <span style={{ fontSize:13, color:'var(--ink-2)', lineHeight:1.5 }}>{h}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Templates de texto por tipo ───────────────────────────────────────────────

function ReportEjecutivo({ a }) {
  return <>
    <AnalysisSection label="INVENTARIO"  icon={Monitor}        iconColor="var(--green)" data={a.activos} />
    <AnalysisSection label="INCIDENTES"  icon={Ticket}         iconColor="var(--amber)" data={a.incidentes} />
    <AnalysisSection label="SEGURIDAD"   icon={Shield}         iconColor="var(--red)" data={a.seguridad} />
    <AnalysisSection label="PARCHES"     icon={RefreshCw}      iconColor="var(--ink-2)" data={a.parches} />
    <AnalysisSection label="LICENCIAS"   icon={Package}        iconColor="var(--ink-2)" data={a.licencias} />
  </>;
}

function ReportSeguridad({ a }) {
  return <>
    <AnalysisSection label="POSTURE DE SEGURIDAD" icon={Shield}       iconColor="var(--red)" data={a.posture} />
    <AnalysisSection label="VULNERABILIDADES"      icon={AlertTriangle} iconColor="var(--red)" data={a.vulnerabilidades} />
    <AnalysisSection label="PARCHES"               icon={RefreshCw}   iconColor="var(--amber)" data={a.parches} />
    {a.activos_criticos && (
      <Section label="ACTIVOS CRÍTICOS" icon={Monitor} iconColor="var(--red)">
        <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 10px' }}>{a.activos_criticos.analisis}</p>
        {a.activos_criticos.lista?.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
            {a.activos_criticos.lista.map((item, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 10px', background:'var(--bg-subtle)', borderRadius:4, border:'1px solid var(--border)', fontSize:12 }}>
                <span style={{ fontFamily:'monospace', fontWeight:600, color:'var(--ink)', flex:1 }}>{item.hostname}</span>
                <span style={{ color:'var(--red)', fontWeight:700 }}>{item.score}/100</span>
                <span style={{ color:'var(--ink-2)' }}>{item.riesgo}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    )}
  </>;
}

function ReportCostos({ a }) {
  return <>
    <AnalysisSection label="COSTOS CLOUD"         icon={DollarSign}  iconColor="var(--amber)" data={a.cloud} />
    <AnalysisSection label="LICENCIAS"             icon={Package}     iconColor="var(--ink-2)" data={a.licencias} />
    <AnalysisSection label="HARDWARE / ACTIVOS"    icon={Monitor}     iconColor="var(--ink-2)" data={a.hardware} />
    {a.oportunidades_ahorro?.length > 0 && (
      <Section label="OPORTUNIDADES DE AHORRO" icon={TrendingUp} iconColor="var(--green)">
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          {a.oportunidades_ahorro.map((op, i) => (
            <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7 }}>
              <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
              <span style={{ fontSize:12, color:'var(--ink-2)' }}>{op}</span>
            </div>
          ))}
        </div>
      </Section>
    )}
  </>;
}

function ReportCapacity({ a }) {
  return <>
    <AnalysisSection label="MEMORIA RAM"          icon={HardDrive}  iconColor="var(--accent)" data={a.ram} />
    <AnalysisSection label="ALMACENAMIENTO"        icon={HardDrive}  iconColor="var(--ink-2)" data={a.almacenamiento} />
    <AnalysisSection label="SISTEMAS OPERATIVOS"   icon={Monitor}    iconColor="var(--ink-2)" data={a.sistemas_operativos} />
    {a.crecimiento && (
      <Section label="TENDENCIA DE CRECIMIENTO" icon={TrendingUp} iconColor="var(--green)">
        <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 8px' }}>{a.crecimiento.analisis}</p>
        {a.crecimiento.proyeccion_6m && (
          <div style={{ padding:'8px 12px', background:'var(--green-dim)', borderRadius:5, border:'1px solid var(--green-dim)', fontSize:12, color:'var(--green)', marginBottom:8 }}>
            <strong>Proyección 6 meses:</strong> {a.crecimiento.proyeccion_6m}
          </div>
        )}
        {a.crecimiento.recomendaciones?.map((r, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7, marginTop:5 }}>
            <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
            <span style={{ fontSize:12, color:'var(--ink-2)' }}>{r}</span>
          </div>
        ))}
      </Section>
    )}
  </>;
}

function ReportIncidentes({ a }) {
  return <>
    <AnalysisSection label="VOLUMEN DE INCIDENTES"   icon={Ticket}        iconColor="var(--ink-2)" data={a.volumen} />
    <AnalysisSection label="PRIORIDADES CRÍTICAS"    icon={AlertTriangle} iconColor="var(--red)" data={a.prioridades_criticas} />
    <AnalysisSection label="TENDENCIA"               icon={TrendingUp}    iconColor="var(--accent)" data={a.tendencia} />
    <AnalysisSection label="ACTIVOS RECURRENTES"     icon={Monitor}       iconColor="var(--amber)" data={a.activos_recurrentes} />
  </>;
}

function ReportCumplimiento({ a }) {
  const pct = (v) => v != null ? `${v}%` : '–';
  return <>
    {a.parches && (
      <Section label="CUMPLIMIENTO DE PARCHES" icon={RefreshCw} iconColor="var(--ink-2)">
        {a.parches.porcentaje_cumplimiento != null && (
          <div style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--ink-2)', marginBottom:4 }}>
              <span>Conformidad</span><span style={{ fontWeight:700, color:'var(--ink)' }}>{pct(a.parches.porcentaje_cumplimiento)}</span>
            </div>
            <div style={{ height:6, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${a.parches.porcentaje_cumplimiento}%`, background: a.parches.porcentaje_cumplimiento >= 80 ? 'var(--green)' : a.parches.porcentaje_cumplimiento >= 50 ? 'var(--amber)' : 'var(--red)', transition:'width 0.5s' }} />
            </div>
          </div>
        )}
        <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 8px' }}>{a.parches.analisis}</p>
        {a.parches.recomendaciones?.map((r, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7, marginTop:4 }}>
            <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
            <span style={{ fontSize:12, color:'var(--ink-2)' }}>{r}</span>
          </div>
        ))}
      </Section>
    )}
    {a.baseline && (
      <Section label="BASELINE DE SEGURIDAD" icon={CheckSquare} iconColor="var(--ink-2)">
        {a.baseline.porcentaje_cumplimiento != null && (
          <div style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--ink-2)', marginBottom:4 }}>
              <span>Conformidad</span><span style={{ fontWeight:700, color:'var(--ink)' }}>{pct(a.baseline.porcentaje_cumplimiento)}</span>
            </div>
            <div style={{ height:6, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${a.baseline.porcentaje_cumplimiento}%`, background: a.baseline.porcentaje_cumplimiento >= 80 ? 'var(--green)' : a.baseline.porcentaje_cumplimiento >= 50 ? 'var(--amber)' : 'var(--red)' }} />
            </div>
          </div>
        )}
        <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 8px' }}>{a.baseline.analisis}</p>
        {a.baseline.recomendaciones?.map((r, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7, marginTop:4 }}>
            <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
            <span style={{ fontSize:12, color:'var(--ink-2)' }}>{r}</span>
          </div>
        ))}
      </Section>
    )}
    <AnalysisSection label="LICENCIAS" icon={Package} iconColor="var(--amber)" data={a.licencias} />
    {a.iso27001 && (
      <Section label="READINESS ISO 27001" icon={Shield} iconColor="var(--ink-2)">
        <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 8px' }}>{a.iso27001.analisis}</p>
        {a.iso27001.areas_criticas?.length > 0 && (
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--ink-3)', marginBottom:5 }}>ÁREAS CRÍTICAS</div>
            {a.iso27001.areas_criticas.map((ar, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7, marginBottom:4 }}>
                <AlertTriangle size={13} style={{ color:'var(--red)', marginTop:2, flexShrink:0 }} />
                <span style={{ fontSize:12, color:'var(--ink-2)' }}>{ar}</span>
              </div>
            ))}
          </div>
        )}
        {a.iso27001.recomendaciones?.map((r, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7, marginTop:4 }}>
            <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
            <span style={{ fontSize:12, color:'var(--ink-2)' }}>{r}</span>
          </div>
        ))}
      </Section>
    )}
  </>;
}

function usd(n) {
  if (n == null) return '–';
  return `US$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function OptAnalysis({ label, icon, iconColor, data }) {
  if (!data) return null;
  return (
    <Section label={label} icon={icon} iconColor={iconColor}>
      <p style={{ fontSize:13, lineHeight:1.65, color:'var(--ink-2)', margin:'0 0 10px' }}>{data.analisis}</p>
      {data.justificacion && (
        <div style={{ padding:'10px 12px', background:'var(--amber-dim)', borderRadius:6, border:'1px solid var(--amber-dim)', fontSize:12, color:'var(--amber)', marginBottom:10 }}>
          <strong>Justificación:</strong> {data.justificacion}
        </div>
      )}
      {data.recomendaciones?.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          {data.recomendaciones.map((r, i) => (
            <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:7 }}>
              <ChevronRight size={13} style={{ color:'var(--green)', marginTop:2, flexShrink:0 }} />
              <span style={{ fontSize:12, color:'var(--ink-2)' }}>{r}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function ReportOptimizacionAws({ a }) {
  return <>
    <div className="card" style={{ padding:'18px 24px', marginBottom:12 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12 }}>
        <MiniCard label="Gasto mensual (mes en curso)" value={usd(a.gasto_mensual_usd)} color="var(--ink)" />
        <MiniCard label="Ahorro potencial mensual"     value={usd(a.ahorro_total_mensual_usd)} color="var(--green)" bg="var(--green-dim)" />
        <MiniCard label="Ahorro potencial anual"       value={usd(a.ahorro_total_anual_usd)}   color="var(--green)" bg="var(--green-dim)" />
      </div>
    </div>
    <OptAnalysis label="RECURSOS IDLE (SUBUTILIZADOS)" icon={TrendingDown} iconColor="var(--amber)" data={a.idle} />
    <OptAnalysis label="DESPERDICIO PURO (RECURSOS HUÉRFANOS)" icon={Trash2} iconColor="var(--red)" data={a.desperdicio} />
    <OptAnalysis label="APAGADO PROGRAMADO (HORARIOS)" icon={Clock} iconColor="var(--accent)" data={a.apagado_programado} />
    <OptAnalysis label="ANÁLISIS DE LOGS" icon={FileText} iconColor="var(--ink-2)" data={a.logs} />
  </>;
}

function ReportSeguridadAws({ a }) {
  return <>
    <div className="card" style={{ padding:'18px 24px', marginBottom:12 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12 }}>
        <MiniCard label="Security Score" value={a.score != null ? `${a.score}/100 · ${a.grade || '—'}` : '—'} color="var(--ink)" />
        <MiniCard label="Nivel de riesgo" value={a.nivel_riesgo || '—'} color="var(--red)" bg="var(--red-dim)" />
        <MiniCard label="Ambiente" value={a.ambiente || '—'} color="var(--ink)" />
      </div>
    </div>
    <OptAnalysis label="IAM — POSTURA DE CUENTA" icon={Shield} iconColor="var(--red)" data={a.iam} />
    <OptAnalysis label="EXPOSICIÓN Y HALLAZGOS" icon={AlertTriangle} iconColor="var(--amber)" data={a.exposicion} />
    <OptAnalysis label="AUDITORÍA (CLOUDTRAIL)" icon={FileText} iconColor="var(--accent)" data={a.auditoria} />
  </>;
}

function ReportDrPlan({ a }) {
  return <>
    <div className="card" style={{ padding:'18px 24px', marginBottom:12 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12 }}>
        <MiniCard label="Cobertura DR" value={a.cobertura_pct != null ? `${a.cobertura_pct}%` : '—'} color="var(--ink-2)" bg="#ecfeff" />
        <MiniCard label="Riesgo de continuidad" value={a.nivel_riesgo || '—'} color="var(--red)" bg="var(--red-dim)" />
        <MiniCard label="Ubicación" value={a.ubicacion || '—'} color="var(--ink)" />
      </div>
    </div>
    <OptAnalysis label="TIERS DE RECUPERACIÓN" icon={LifeBuoy} iconColor="var(--ink-2)" data={a.tiers} />
    <OptAnalysis label="GAPS DE CONTINUIDAD" icon={AlertTriangle} iconColor="var(--red)" data={a.gaps} />
    {a.checklist_recuperacion?.length > 0 && (
      <Section label="CHECKLIST DE RECUPERACIÓN" icon={CheckSquare} iconColor="var(--green)">
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {a.checklist_recuperacion.map((s, i) => (
            <div key={i} style={{ display:'flex', gap:8, fontSize:13, color:'var(--ink-2)', alignItems:'flex-start' }}>
              <span style={{ fontFamily:'monospace', color:'var(--green)', fontWeight:700, flexShrink:0 }}>{i + 1}.</span>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </Section>
    )}
  </>;
}

const REPORT_BODY = {
  ejecutivo:    ReportEjecutivo,
  seguridad:    ReportSeguridad,
  costos:       ReportCostos,
  capacity:     ReportCapacity,
  incidentes:   ReportIncidentes,
  cumplimiento: ReportCumplimiento,
  optimizacion_aws: ReportOptimizacionAws,
  seguridad_aws:    ReportSeguridadAws,
  dr_plan:          ReportDrPlan,
};

// ── Panel: payload ofuscado enviado a la IA (solo DEV/Demo) ────────────────────

function ObfuscatedPanel({ reportId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  useEffect(() => {
    let alive = true;
    apiFetch(`/ai-reports/${reportId}/obfuscated`)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e.message || 'Error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reportId]);

  return (
    <div className="card" style={{ padding:'18px 22px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <Shield size={16} style={{ color:'var(--green)' }} />
        <span style={{ fontSize:13, fontWeight:700, color:'var(--ink, var(--ink))' }}>Datos enviados a la IA (ofuscados)</span>
      </div>
      <p style={{ fontSize:12.5, color:'var(--ink-3, var(--ink-2))', lineHeight:1.6, margin:'0 0 12px' }}>
        Esto es exactamente lo que sale del ambiente hacia la IA. Los identificadores reales
        (equipos, usuarios, IPs, emails) se reemplazan por tokens opacos
        (<code>ACTIVO_1</code>, <code>USUARIO_2</code>, <code>IP_3</code>, <code>[EMAIL]</code>)
        antes de enviarse; la respuesta se de-tokeniza recién al mostrártela. Ningún dato real sale del ambiente.
        {data && <span> · Modo: <b>{data.mode === 'tokenize' ? 'Tokenización + scrub' : 'Solo scrub'}</b>
          {data.enabled === false && <b style={{ color:'var(--red)' }}> — ofuscación DESACTIVADA</b>}</span>}
      </p>
      {loading ? <div style={{ color:'var(--ink-3)', fontSize:13 }}>Generando vista ofuscada…</div>
        : err ? <div style={{ color:'var(--red)', fontSize:13 }}>{err}</div>
        : (
          <pre style={{ margin:0, background:'var(--ink)', color:'var(--border)', borderRadius:8, padding:'14px 16px',
            fontSize:12, lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word', maxHeight:480, overflowY:'auto',
            fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            {data?.preview || '(vacío)'}
          </pre>
        )}
    </div>
  );
}

// ── Vista del informe ─────────────────────────────────────────────────────────

function ReportView({ report }) {
  const [tab, setTab] = useState('informe');
  const [variant, setVariant] = useState('detallado');
  const a    = report.analysis;
  const raw  = report.raw_data;
  const type = REPORT_TYPES.find(t => t.id === (report.report_type || 'ejecutivo')) || REPORT_TYPES[0];
  const isOpt = type.id === 'optimizacion_aws';
  const isSecAws = type.id === 'seguridad_aws';
  const isDr = type.id === 'dr_plan';
  const badgeLabel = isOpt ? 'Oportunidad de ahorro' : isDr ? 'Riesgo de continuidad' : 'Nivel de riesgo';
  const badgeValue = isOpt ? a?.oportunidad_ahorro : a?.nivel_riesgo;
  const risk = RISK_CFG[badgeValue] || RISK_CFG['MEDIO'];
  const title = isOpt ? `Optimización AWS · ${a?.ambiente || ''}`
    : isSecAws ? `Seguridad AWS · ${a?.ambiente || ''}`
    : isDr ? `Plan de DR · ${a?.ubicacion || ''}`
    : 'Estado de la Infraestructura';
  const Body = REPORT_BODY[type.id]  || ReportEjecutivo;
  const Inf  = INFOGRAFIA[type.id]   || InfografiaEjecutivo;
  const Detalle = DETALLE[type.id]   || null;

  if (!a) return null;

  return (
    <div id="ai-report-print">
      {/* Header */}
      <div style={{ background:'var(--surface)', borderRadius:10, border:'1px solid var(--border)', borderLeft:`4px solid ${BRAND}`, padding:'20px 28px', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:40, height:40, borderRadius:8, background: type.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <type.icon size={20} style={{ color: type.color }} />
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.1em', color:BRAND, marginBottom:3 }}>{type.label.toUpperCase()}</div>
              <div style={{ fontSize:18, fontWeight:800, color:'var(--ink)', lineHeight:1.2 }}>{title}</div>
              <div style={{ fontSize:12, color:'var(--ink-3)', marginTop:3 }}>
                {report.period_label} · {new Date(report.created_at).toLocaleString('es-AR')}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:9, fontWeight:700, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{badgeLabel}</div>
              <div style={{ fontSize:16, fontWeight:800, color:risk.color,}}>{badgeValue}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:16, border:'1px solid var(--border)', borderRadius:8, overflow:'hidden', width:'fit-content' }}>
        {[
          { id:'informe',    label:'Ejecutivo',  icon: Brain },
          ...(Detalle ? [{ id:'detalle', label:'Detallado', icon: BarChart2 }] : []),
          { id:'infografia', label:'Infografía', icon: BarChart2 },
          ...(report.obfuscation_preview_available ? [{ id:'ofuscado', label:'Enviado a la IA', icon: Shield }] : []),
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 18px', border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
              background: tab === t.id ? BRAND : '#fff',
              color:      tab === t.id ? '#fff' : 'var(--ink-2)',
              transition:'all 0.15s' }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'informe' && (
        <>
          {/* Resumen ejecutivo */}
          <div className="card" style={{ padding:'20px 24px', marginBottom:12 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', color:'var(--ink-3)', marginBottom:8 }}>RESUMEN EJECUTIVO</div>
            <p style={{ fontSize:14, lineHeight:1.7, color:'var(--ink-2)', margin:0 }}>{a.resumen_ejecutivo}</p>
          </div>
          <Findings items={a.hallazgos_criticos} riskColor={risk.color} />
          <Body a={a} />
          <Priorities items={a.prioridades} />
        </>
      )}
      {tab === 'detalle' && Detalle && (
        <>
          {STD_DASH_TYPES.includes(type.id) && (
            <div style={{ display:'flex', gap:0, marginBottom:12, border:'1px solid var(--border)', borderRadius:8, overflow:'hidden', width:'fit-content' }}>
              {[{ id:'basico', label:'Básico' }, { id:'detallado', label:'Detallado' }].map(v => (
                <button key={v.id} onClick={() => setVariant(v.id)}
                  style={{ padding:'6px 16px', border:'none', cursor:'pointer', fontSize:12.5, fontWeight:600,
                    background: variant === v.id ? BRAND : '#fff', color: variant === v.id ? '#fff' : 'var(--ink-2)' }}>
                  {v.label}
                </button>
              ))}
            </div>
          )}
          <Detalle a={a} raw={raw} report={report} variant={variant} />
        </>
      )}
      {tab === 'infografia' && <Inf a={a} raw={raw} />}
      {tab === 'ofuscado' && report.obfuscation_preview_available && <ObfuscatedPanel reportId={report.id} />}

      <div style={{ textAlign:'center', fontSize:10, color:'var(--ink-3)', marginTop:16, paddingTop:14, borderTop:'1px solid var(--border)' }}>
        Generado por NODWARD AI · {new Date(report.created_at).toLocaleString('es-AR')} · {report.generated_by_name || 'Sistema'}
      </div>
    </div>
  );
}

// ── Grilla de tipos de informe ────────────────────────────────────────────────

function LangToggle({ value, onChange }) {
  return (
    <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:6, overflow:'hidden', fontSize:12, fontWeight:600 }}>
      {[{ id:'es', label:'🇦🇷 Español' }, { id:'en', label:'🇺🇸 English' }].map(l => (
        <button key={l.id} onClick={() => onChange(l.id)}
          style={{ padding:'6px 14px', border:'none', cursor:'pointer', background: value === l.id ? 'var(--ink)' : '#fff', color: value === l.id ? '#fff' : 'var(--ink-2)', transition:'all 0.1s' }}>
          {l.label}
        </button>
      ))}
    </div>
  );
}

// Mensajes de espera rotativos durante la generación
const WAIT_MSGS = [
  'Consultando la IA…',
  'Analizando el inventario de activos…',
  'Evaluando el estado de seguridad…',
  'Procesando incidentes y métricas…',
  'Elaborando recomendaciones…',
  'Generando el informe final…',
];

function GeneratingOverlay({ typeId }) {
  const [msgIdx, setMsgIdx] = useState(0);
  const type = REPORT_TYPES.find(t => t.id === typeId) || REPORT_TYPES[0];
  useEffect(() => {
    const iv = setInterval(() => setMsgIdx(i => (i + 1) % WAIT_MSGS.length), 5000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'60px 20px', gap:16 }}>
      <div style={{ width:56, height:56, borderRadius:12, background:type.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <RefreshCw size={26} style={{ color:type.color, animation:'spin 0.8s linear infinite' }} />
      </div>
      <div style={{ fontSize:15, fontWeight:700, color:'var(--ink)' }}>Generando {type.label}…</div>
      <div style={{ fontSize:13, color:'var(--ink-2)', minHeight:20, transition:'opacity 0.3s' }}>{WAIT_MSGS[msgIdx]}</div>
      <div style={{ fontSize:11, color:'var(--ink-3)' }}>Esto puede tardar hasta 60 segundos</div>
    </div>
  );
}

function TypeGrid({ onSelect, generating, language, onLanguageChange }) {
  const { user } = useAuth();
  const isSeguridad = user?.role === 'seguridad';
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [awsAccountId, setAwsAccountId] = useState('');
  const [logGroups, setLogGroups] = useState([]);
  const [logGroupsLoading, setLogGroupsLoading] = useState(false);
  const [awsLogGroup, setAwsLogGroup] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');

  useEffect(() => {
    apiFetch('/aws/inventory/accounts')
      .then(d => setAwsAccounts(Array.isArray(d) ? d : []))
      .catch(() => {});
    apiFetch('/locations')
      .then(d => setLocations(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Cargar log groups cuando se elige ambiente (para el ítem opcional de logs)
  useEffect(() => {
    setLogGroups([]); setAwsLogGroup('');
    if (!awsAccountId) return;
    setLogGroupsLoading(true);
    apiFetch(`/aws/inventory/log-groups?accountId=${awsAccountId}`)
      .then(d => setLogGroups(d.groups || []))
      .catch(() => {})
      .finally(() => setLogGroupsLoading(false));
  }, [awsAccountId]);

  return (
    <div>
      <div className="page-header" style={{ marginBottom:20 }}>
        <div>
          <div className="page-title">Informes IA</div>
          <div className="page-sub">Seleccioná el tipo de informe a generar</div>
        </div>
        <LangToggle value={language} onChange={onLanguageChange} />
      </div>

      {generating && (
        <div className="card" style={{ marginBottom:16 }}>
          <GeneratingOverlay typeId={generating} />
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:12 }}>
        {REPORT_TYPES
          .filter(t => !isSeguridad || SECURITY_REPORT_IDS.includes(t.id))
          .filter(t => !t.needsAccount || awsAccounts.length > 0).map(type => {
          const Icon = type.icon;
          const isGenerating = generating === type.id;

          // Informe que se filtra por ubicación (matriz de criticidad)
          if (type.needsLocation) {
            const canGen = !generating;
            return (
              <div key={type.id} className="card" style={{ padding:'20px 22px', opacity: generating && !isGenerating ? 0.5 : 1 }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:14, marginBottom:12 }}>
                  <div style={{ width:42, height:42, borderRadius:8, background:type.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isGenerating
                      ? <RefreshCw size={20} style={{ color:type.color, animation:'spin 0.8s linear infinite' }} />
                      : <Icon size={20} style={{ color:type.color }} />}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'var(--ink)', marginBottom:4 }}>{type.label}</div>
                    <div style={{ fontSize:12, color:'var(--ink-2)', lineHeight:1.4 }}>{type.desc}</div>
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <select value={locationId} onChange={e => setLocationId(e.target.value)} disabled={!!generating}
                    style={{ width:'100%', boxSizing:'border-box', fontSize:13, border:'1px solid var(--border-2)', borderRadius:6, padding:'7px 10px', background: 'var(--surface)', color:'var(--ink)', cursor:'pointer' }}>
                    <option value="">Todas las ubicaciones</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                  <button className="btn btn-primary" disabled={!canGen}
                    onClick={() => canGen && onSelect(type.id, undefined, undefined, locationId || undefined)}
                    style={{ width:'100%', boxSizing:'border-box', justifyContent:'center', gap:6, opacity: canGen ? 1 : 0.5, cursor: canGen ? 'pointer' : 'not-allowed' }}>
                    <Sparkles size={13} /> Generar
                  </button>
                </div>
              </div>
            );
          }

          // Informe que requiere elegir cuenta/ambiente AWS
          if (type.needsAccount) {
            const canGenerate = !!awsAccountId && !generating;
            return (
              <div key={type.id} className="card" style={{ padding:'20px 22px', opacity: generating && !isGenerating ? 0.5 : 1 }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:14, marginBottom:12 }}>
                  <div style={{ width:42, height:42, borderRadius:8, background:type.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isGenerating
                      ? <RefreshCw size={20} style={{ color:type.color, animation:'spin 0.8s linear infinite' }} />
                      : <Icon size={20} style={{ color:type.color }} />
                    }
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'var(--ink)', marginBottom:4 }}>{type.label}</div>
                    <div style={{ fontSize:12, color:'var(--ink-2)', lineHeight:1.4 }}>{type.desc}</div>
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <select value={awsAccountId} onChange={e => setAwsAccountId(e.target.value)} disabled={!!generating}
                    style={{ width:'100%', boxSizing:'border-box', fontSize:13, border:'1px solid var(--border-2)', borderRadius:6, padding:'7px 10px', background: 'var(--surface)', color:'var(--ink)', cursor:'pointer' }}>
                    <option value="">— Elegir ambiente —</option>
                    {awsAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.environment || 'sin ambiente'} · {a.name}</option>
                    ))}
                  </select>
                  {type.id === 'optimizacion_aws' && (
                  <select value={awsLogGroup} onChange={e => setAwsLogGroup(e.target.value)} disabled={!!generating || !awsAccountId}
                    style={{ width:'100%', boxSizing:'border-box', fontSize:13, border:'1px solid var(--border-2)', borderRadius:6, padding:'7px 10px', background: 'var(--surface)', color:'var(--ink)', cursor:'pointer' }}>
                    <option value="">{logGroupsLoading ? 'Cargando log groups…' : 'Log group para análisis (opcional)'}</option>
                    {logGroups.map(g => (
                      <option key={g.name} value={g.name}>{g.name}</option>
                    ))}
                  </select>
                  )}
                  <button className="btn btn-primary" disabled={!canGenerate}
                    onClick={() => canGenerate && onSelect(type.id, awsAccountId, awsLogGroup)}
                    style={{ width:'100%', boxSizing:'border-box', justifyContent:'center', gap:6, opacity: canGenerate ? 1 : 0.5, cursor: canGenerate ? 'pointer' : 'not-allowed' }}>
                    <Sparkles size={13} /> Generar
                  </button>
                </div>
                {awsAccounts.length === 0 && (
                  <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:8 }}>No hay cuentas AWS configuradas en Integraciones.</div>
                )}
              </div>
            );
          }

          return (
            <div key={type.id} className="card" style={{ padding:'20px 22px', cursor: isGenerating ? 'not-allowed' : generating ? 'not-allowed' : 'pointer', opacity: generating && !isGenerating ? 0.5 : 1, transition:'box-shadow 0.15s' }}
              onClick={() => !generating && onSelect(type.id)}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                <div style={{ width:42, height:42, borderRadius:8, background:type.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {isGenerating
                    ? <RefreshCw size={20} style={{ color:type.color, animation:'spin 0.8s linear infinite' }} />
                    : <Icon size={20} style={{ color:type.color }} />
                  }
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'var(--ink)', marginBottom:4 }}>{type.label}</div>
                  <div style={{ fontSize:12, color:'var(--ink-2)', lineHeight:1.4 }}>{type.desc}</div>
                </div>
                <ChevronRight size={16} style={{ color:'var(--border-2)', flexShrink:0, marginTop:4 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Historial ─────────────────────────────────────────────────────────────────

function HistoryPanel({ reports, selected, onSelect, onDelete }) {
  const byType = REPORT_TYPES.reduce((acc, t) => {
    acc[t.id] = reports.filter(r => (r.report_type || 'ejecutivo') === t.id);
    return acc;
  }, {});

  return (
    <div style={{ width:260, flexShrink:0 }}>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', color:'var(--ink-3)', marginBottom:10 }}>HISTORIAL</div>
      {REPORT_TYPES.map(type => {
        const items = byType[type.id] || [];
        if (!items.length) return null;
        const Icon = type.icon;
        return (
          <div key={type.id} style={{ marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
              <Icon size={12} style={{ color:type.color }} />
              <span style={{ fontSize:11, fontWeight:600, color:'var(--ink-2)' }}>{type.label}</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {items.slice(0, 5).map(r => {
                const risk = RISK_CFG[r.nivel_riesgo];
                const isActive = selected?.id === r.id;
                return (
                  <div key={r.id} onClick={() => onSelect(r.id)}
                    style={{ padding:'8px 10px', border:`1px solid ${isActive ? type.color : 'var(--border)'}`, borderRadius:5, cursor:'pointer', background: isActive ? type.bg : '#fff', display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:11, fontWeight:600, color:'var(--ink)', marginBottom:2 }}>{r.period_label}</div>
                      <div style={{ fontSize:10, color:'var(--ink-3)', display:'flex', alignItems:'center', gap:4 }}>
                        <Clock size={9} />
                        {new Date(r.created_at).toLocaleDateString('es-AR', { day:'2-digit', month:'short' })}
                        <span style={{ fontSize:9, background:'var(--bg)', color:'var(--ink-2)', padding:'0 4px', borderRadius:3 }}>{r.language === 'en' ? 'EN' : 'ES'}</span>
                      </div>
                    </div>
                    {risk && <span style={{ fontSize:9, fontWeight:700, color:risk.color, flexShrink:0 }}>{r.nivel_riesgo}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {reports.length === 0 && (
        <div style={{ textAlign:'center', padding:20, color:'var(--ink-3)', fontSize:12 }}>Sin informes generados</div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export default function AIReports() {
  const { user } = useAuth();
  const [reports,    setReports]    = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [generating, setGenerating] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [view,       setView]       = useState('grid');
  const [language,   setLanguage]   = useState('es');
  const [aiPrivacy,  setAiPrivacy]  = useState(null); // { enabled, mode } — solo admin

  useEffect(() => {
    apiFetch('/ai-reports')
      .then(setReports)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    if (user?.role === 'admin') apiFetch('/ai-reports/privacy').then(setAiPrivacy).catch(() => {});
  }, []); // eslint-disable-line

  const togglePrivacy = async () => {
    const next = { enabled: !(aiPrivacy?.enabled), mode: aiPrivacy?.mode || 'tokenize' };
    setAiPrivacy(next);
    try { await apiFetch('/ai-reports/privacy', { method: 'PUT', body: JSON.stringify(next) }); }
    catch (e) { setError(e.message); setAiPrivacy(aiPrivacy); }
  };

  const generate = async (typeId, accountId, logGroup, locationId) => {
    setGenerating(typeId);
    setError('');
    try {
      const report = await apiFetch('/ai-reports/generate', { method:'POST', body: JSON.stringify({ type: typeId, language, accountId, logGroup, locationId }) });
      // El endpoint responde 200 con heartbeat; los errores viajan como {error} en el body
      if (report?.error) throw new Error(report.error);
      setReports(prev => [report, ...prev]);
      setSelected(report);
      setView('report');
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(null);
    }
  };

  const loadReport = async (id) => {
    try {
      const data = await apiFetch(`/ai-reports/${id}`);
      setSelected(data);
      setView('report');
    } catch (e) { setError(e.message); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('¿Eliminar este informe?')) return;
    try {
      await apiFetch(`/ai-reports/${id}`, { method:'DELETE' });
      setReports(prev => prev.filter(r => r.id !== id));
      if (selected?.id === id) { setSelected(null); setView('grid'); }
    } catch (e) { setError(e.message); }
  };

  if (loading) return <div style={{ textAlign:'center', padding:40, color:'var(--ink-3)' }}>Cargando…</div>;

  return (
    <div style={{ maxWidth:1100 }}>
      {error && <div className="alert alert-error" style={{ marginBottom:14, fontSize:12 }}>{error}</div>}

      {view === 'grid' ? (
        <>
        {user?.role === 'admin' && aiPrivacy && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, background:'var(--bg-subtle)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>Ofuscar datos enviados a la IA</div>
              <div style={{ fontSize:12, color:'var(--ink-2)', marginTop:2 }}>
                Tokeniza hostnames, usuarios e IPs antes de enviarlos al proveedor de IA; la respuesta se revierte a los nombres reales. {aiPrivacy.enabled ? 'Activado.' : 'Desactivado.'}
              </div>
            </div>
            <button onClick={togglePrivacy} aria-label="Ofuscar datos a la IA" style={{ width:44, height:24, borderRadius:12, border:'none', cursor:'pointer', background: aiPrivacy.enabled ? 'var(--accent)' : 'var(--ink-3)', position:'relative', flexShrink:0 }}>
              <span style={{ position:'absolute', top:2, left: aiPrivacy.enabled ? 22 : 2, width:20, height:20, borderRadius:'50%', background:'var(--surface)', transition:'left 0.15s' }} />
            </button>
          </div>
        )}
        <div style={{ display:'flex', gap:24, alignItems:'flex-start' }}>
          <div style={{ flex:1 }}>
            <TypeGrid onSelect={generate} generating={generating} language={language} onLanguageChange={setLanguage} />
          </div>
          {reports.length > 0 && (
            <HistoryPanel reports={reports} selected={selected} onSelect={loadReport} onDelete={deleteReport} />
          )}
        </div>
        </>
      ) : (
        <div>
          {/* Toolbar */}
          <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
            <button onClick={() => setView('grid')} className="btn btn-secondary" style={{ gap:6 }}>
              <ArrowLeft size={13} /> Volver
            </button>
            <button onClick={() => {
              // Si está el dashboard AWS (tab Detallado), imprimir ese HTML autocontenido directamente
              const frame = document.getElementById('aws-dashboard-frame');
              if (frame && frame.contentWindow) {
                frame.contentWindow.focus();
                frame.contentWindow.print();
                return;
              }
              const el = document.getElementById('ai-report-print');
              if (!el) return;
              const win = window.open('', '_blank', 'width=900,height=700');
              win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Informe IT</title>
                <style>
                  * { box-sizing: border-box; margin: 0; padding: 0; }
                  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; color: var(--ink); background: #fff; padding: 20px; }
                  @media print { @page { margin: 15mm; } body { padding: 0; } }
                </style>
              </head><body>${el.innerHTML}</body></html>`);
              win.document.close();
              win.focus();
              setTimeout(() => { win.print(); win.close(); }, 500);
            }} className="btn btn-secondary" style={{ gap:6 }}>
              <Printer size={13} /> Imprimir / PDF
            </button>
            {reports.length > 1 && (
              <select onChange={e => loadReport(e.target.value)} value={selected?.id || ''}
                style={{ fontSize:12, padding:'5px 8px', border:'1px solid var(--border)', borderRadius:4, color:'var(--ink-2)', marginLeft:4 }}>
                {reports.map(r => {
                  const t = REPORT_TYPES.find(x => x.id === (r.report_type||'ejecutivo'));
                  return <option key={r.id} value={r.id}>{t?.label} — {r.period_label} · {new Date(r.created_at).toLocaleDateString('es-AR')}</option>;
                })}
              </select>
            )}
            <div style={{ display:'flex', gap:8, marginLeft:'auto' }}>
              <button onClick={() => setView('grid')} className="btn btn-primary" style={{ gap:6 }}>
                <Sparkles size={13} /> Nuevo informe
              </button>
              <button onClick={() => deleteReport(selected?.id)} className="btn btn-danger" style={{ gap:6 }}>
                <Trash2 size={13} /> Eliminar
              </button>
            </div>
          </div>
          {selected && <ReportView report={selected} />}
        </div>
      )}

      <style>{`
        @media print {
          body > * { display: none !important; }
          #ai-report-print { display: block !important; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
